import { useState, useCallback, useMemo, useRef } from "react";

import { useProjectDetailsContext } from "../context";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  CalendarDays,
  Receipt,
  Calculator,
  Pencil,
  Check,
  X,
  Lock,
  LockOpen,
  ChevronRight,
  BarChart3,
  Clock,
  Bell,
  Trash2,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, calculateEAC } from "@/lib/utils";
import { useEacSettings } from "@/contexts/EacSettingsContext";

// ─── Types ────────────────────────────────────────────────────────────────────

type MonthKey = string; // "YYYY-MM"

// Project billing type — drives the Revenue sub-tab structure.
// In production this comes from the project record (project.type).
type ProjectType = "time-and-materials" | "fixed-price" | "retainer";

interface CostRow {
  id: string;
  label: string;
  budget: number;
  values: Record<MonthKey, number>;
}

interface CostGroup {
  id: string;
  label: string;
  rows: CostRow[];
}

// T&M revenue — flat category rows mirroring cost structure
interface TmRevenueRow {
  id: string;
  label: string;
  budget: number;
  values: Record<MonthKey, number>;
}

// Fixed-price revenue — milestone rows
interface MilestoneRow {
  id: string;
  name: string;
  amount: number;
  forecastDate: MonthKey | null; // "YYYY-MM" — PM's expected invoice month
  contractValue: number;
}

interface EditingCell {
  rowId: string;
  month: MonthKey;
}

// Roll-forward row types
interface RollForwardCostRow {
  id: string;
  label: string;
  gridRowId: string;
  forecast: number;
  actual: number;
  unspent: number;
}

interface RollForwardRevenueRow {
  id: string;
  label: string;
  gridRowId: string;
  forecast: number;
  actualInvoiced: number;
  uninvoiced: number;
}

type RollForwardAction = "Confirm" | "Zero" | "Redistribute";

interface RollForwardRowState {
  action: RollForwardAction;
  moveTo: MonthKey;
}

// ─── Date / month utilities ───────────────────────────────────────────────────

function toMonthKey(date: Date): MonthKey {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function addMonths(date: Date, n: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

function monthKeyToDate(mk: MonthKey): Date {
  const [y, m] = mk.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, 1);
}

function generateMonths(start: Date, end: Date): MonthKey[] {
  const months: MonthKey[] = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endKey = toMonthKey(new Date(end.getFullYear(), end.getMonth(), 1));
  while (toMonthKey(cur) <= endKey) {
    months.push(toMonthKey(cur));
    cur = addMonths(cur, 1);
  }
  return months;
}

function formatMonthLabel(month: MonthKey): string {
  const [year, mo] = month.split("-");
  return new Date(Number(year), Number(mo) - 1, 1).toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
  });
}

function formatDate(iso: string): string {
  const parts = iso.split("-").map(Number);
  return new Date(parts[0], (parts[1] ?? 1) - 1, parts[2] ?? 1).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// ─── Budget mode type ─────────────────────────────────────────────────────────
// In production, read from OrgBudgetConfiguration.BudgetMode via useBudgetPermissions().
// isCostCentreMode → 'cost-centre' | isCategoryMode → 'category'
type BudgetMode = "category" | "cost-centre";

// ─── Cost mock-data builders ──────────────────────────────────────────────────

interface CostRowDef {
  groupId: string;
  id: string;
  label: string;
  budget: number;
  monthlyActual: number;
  monthlyForecast: number;
}

const COST_ROW_DEFS: CostRowDef[] = [
  { groupId: "labour",         id: "pm",          label: "Project Manager",      budget: 70_000, monthlyActual: 5_800, monthlyForecast: 5_500 },
  { groupId: "labour",         id: "senior-dev",  label: "Senior Developer",     budget: 55_000, monthlyActual: 4_200, monthlyForecast: 4_500 },
  { groupId: "labour",         id: "junior-dev",  label: "Junior Developer",     budget: 40_000, monthlyActual: 2_800, monthlyForecast: 3_000 },
  { groupId: "labour",         id: "ba",          label: "Business Analyst",     budget: 30_000, monthlyActual: 2_100, monthlyForecast: 2_500 },
  { groupId: "labour",         id: "ux",          label: "UX Designer",          budget: 20_000, monthlyActual: 1_500, monthlyForecast: 1_500 },
  { groupId: "expenses",       id: "travel",      label: "Travel & Subsistence", budget: 15_000, monthlyActual:   900, monthlyForecast: 1_000 },
  { groupId: "expenses",       id: "software",    label: "Software & Licences",  budget: 10_000, monthlyActual:   600, monthlyForecast:   800 },
  { groupId: "subcontractors", id: "external",    label: "External Consultant",  budget: 35_000, monthlyActual: 2_000, monthlyForecast: 3_500 },
  { groupId: "contingency",    id: "contingency", label: "Contingency Reserve",  budget: 10_000, monthlyActual:     0, monthlyForecast: 1_500 },
];

const GROUP_META = [
  { id: "labour",         label: "Labour" },
  { id: "expenses",       label: "Expenses" },
  { id: "subcontractors", label: "Sub-contractors" },
  { id: "contingency",    label: "Contingency" },
] as const;

// ─── Cost Centre mock data (used when BudgetMode = cost-centre) ───────────────

interface CostCentreRowDef {
  groupId: string;
  id: string;
  label: string; // includes CC code, e.g. "CC-001 · Projects & Delivery"
  budget: number;
  monthlyActual: number;
  monthlyForecast: number;
}

const CC_ROW_DEFS: CostCentreRowDef[] = [
  { groupId: "engineering", id: "cc-001", label: "CC-001 · Projects & Delivery", budget: 90_000, monthlyActual: 7_200, monthlyForecast: 8_000 },
  { groupId: "engineering", id: "cc-002", label: "CC-002 · Design & UX",         budget: 45_000, monthlyActual: 3_500, monthlyForecast: 3_800 },
  { groupId: "commercial",  id: "cc-003", label: "CC-003 · Business Development", budget: 35_000, monthlyActual: 2_400, monthlyForecast: 2_800 },
  { groupId: "commercial",  id: "cc-004", label: "CC-004 · Bid Management",       budget: 20_000, monthlyActual: 1_200, monthlyForecast: 1_500 },
  { groupId: "overhead",    id: "cc-005", label: "CC-005 · Administration",       budget: 15_000, monthlyActual:   900, monthlyForecast: 1_000 },
  { groupId: "overhead",    id: "cc-006", label: "CC-006 · QHSE",                 budget: 10_000, monthlyActual:   600, monthlyForecast:   700 },
];

const CC_GROUP_META = [
  { id: "engineering", label: "Engineering" },
  { id: "commercial",  label: "Commercial" },
  { id: "overhead",    label: "Overhead" },
] as const;

function buildInitialCostCentreGroups(months: MonthKey[], lastActualsMonth: MonthKey): CostGroup[] {
  return CC_GROUP_META.map((group) => ({
    id: group.id,
    label: group.label,
    rows: CC_ROW_DEFS.filter((r) => r.groupId === group.id).map((def, ri) => {
      const values: Record<MonthKey, number> = {};
      months.forEach((m, mi) => {
        const base = m <= lastActualsMonth ? def.monthlyActual : def.monthlyForecast;
        const variation = Math.round(base * 0.06 * Math.sin((mi + ri + 5) * 1.7));
        values[m] = Math.max(0, base + variation);
      });
      return { id: def.id, label: def.label, budget: def.budget, values };
    }),
  }));
}

function buildInitialCostGroups(months: MonthKey[], lastActualsMonth: MonthKey): CostGroup[] {
  return GROUP_META.map((group) => ({
    id: group.id,
    label: group.label,
    rows: COST_ROW_DEFS.filter((r) => r.groupId === group.id).map((def, ri) => {
      const values: Record<MonthKey, number> = {};
      months.forEach((m, mi) => {
        const base = m <= lastActualsMonth ? def.monthlyActual : def.monthlyForecast;
        const variation = Math.round(base * 0.08 * Math.sin((mi + ri) * 1.3));
        values[m] = Math.max(0, base + variation);
      });
      return { id: def.id, label: def.label, budget: def.budget, values };
    }),
  }));
}

// ─── T&M Revenue mock-data builder ───────────────────────────────────────────
// Rows sourced from SalesBudget → SalesCategories (grouped by billing category)
// Mirrors the CostGroup[] structure so CostForecastGrid can be reused directly.

interface TmRevRowDef {
  groupId: string;
  id: string;
  label: string;
  budget: number;
  monthlyActual: number;
  monthlyForecast: number;
}

const TM_REV_ROW_DEFS: TmRevRowDef[] = [
  // Labour Revenue — from approved timesheets billed at rate card
  { groupId: "labour-rev",   id: "internal-labour-rev", label: "Internal Labour Revenue",  budget: 215_000, monthlyActual: 8_500, monthlyForecast: 12_000 },
  { groupId: "labour-rev",   id: "external-labour-rev", label: "External Labour Revenue",   budget:  80_000, monthlyActual: 3_500, monthlyForecast:  4_500 },
  // Other Revenue — pass-through costs billed to client
  { groupId: "other-rev",    id: "third-party-rev",     label: "3rd Party Revenue",          budget:  40_000, monthlyActual: 1_200, monthlyForecast:  2_000 },
  { groupId: "other-rev",    id: "expense-recovery",    label: "Expense Recovery",            budget:  15_000, monthlyActual:   400, monthlyForecast:    600 },
];

const TM_REV_GROUP_META = [
  { id: "labour-rev", label: "Labour Revenue" },
  { id: "other-rev",  label: "Other Revenue"  },
] as const;

function buildInitialTmRevenueGroups(months: MonthKey[], lastActualsMonth: MonthKey): CostGroup[] {
  return TM_REV_GROUP_META.map((group) => ({
    id: group.id,
    label: group.label,
    rows: TM_REV_ROW_DEFS.filter((r) => r.groupId === group.id).map((def, ri) => {
      const values: Record<MonthKey, number> = {};
      months.forEach((m, mi) => {
        const base = m <= lastActualsMonth ? def.monthlyActual : def.monthlyForecast;
        const variation = Math.round(base * 0.07 * Math.sin((mi + ri + 3) * 1.4));
        values[m] = Math.max(0, base + variation);
      });
      return { id: def.id, label: def.label, budget: def.budget, values };
    }),
  }));
}

// kept for reference — no longer used in flat form
const _TM_REVENUE_ROW_DEFS_LEGACY = [
  { id: "labour-rev",   label: "Internal Labour Revenue",   budget: 215_000, monthlyActual: 8_500,  monthlyForecast: 12_000 },
  { id: "external-rev", label: "External Labour Revenue",   budget:  80_000, monthlyActual: 3_500,  monthlyForecast:  4_500 },
  { id: "third-rev",    label: "3rd Party Revenue",         budget:  25_000, monthlyActual:   800,  monthlyForecast:  1_200 },
]; // end legacy array

// ─── Fixed-price milestone mock data ─────────────────────────────────────────

// forecastDate uses "YYYY-MM" — null = unscheduled (no Forecast Date set by PM)
const INITIAL_MILESTONE_ROWS: MilestoneRow[] = [
  { id: "mobilisation",   name: "Mobilisation",   amount: 25_000, forecastDate: "2027-02", contractValue: 25_000 },
  { id: "design-signoff", name: "Design Sign-off", amount: 40_000, forecastDate: "2027-04", contractValue: 40_000 },
  { id: "final-delivery", name: "Final Delivery",  amount: 35_000, forecastDate: null,       contractValue: 35_000 },
];

// ─── Standalone mock — from FINS ──────────────────────────────────────────────
const MOCK_ACTUAL_INVOICED_TM    = 135_000;
const MOCK_ACTUAL_INVOICED_FP    = 0; // no milestones invoiced yet in mock

// ─── Mock HTC (Hours to Complete) data ───────────────────────────────────────
// In production: read from GET /projects/{id}/forecast/hours-to-complete
// and GET /api/settings/timesheet for showHoursToComplete flag.
const MOCK_HTC_DATA = {
  showHoursToComplete: true,          // reads from mockFinanceSettings (Prompt 6f)
  projectTotalHoursToComplete: 142,
  costRate: 85,                       // £/hr — mock only
  remainingCapacityHours: 120,
  rows: [
    { label: "Internal Labour",  hoursRemaining: 58   },
    { label: "Design",           hoursRemaining: 0    },
    { label: "Offshore Techs",   hoursRemaining: 22   },
    { label: "External Labour",  hoursRemaining: null },
    { label: "OSB",              hoursRemaining: null },
  ] as Array<{ label: string; hoursRemaining: number | null }>,
};

// ─── Mock roll-forward data ───────────────────────────────────────────────────

const MOCK_RF_COST_ROWS: RollForwardCostRow[] = [
  { id: "rf-3rd-party", label: "3rd Party Costs",  gridRowId: "software", forecast: 5_000, actual:   796, unspent: 4_204 },
  { id: "rf-external",  label: "External Labour",   gridRowId: "external", forecast: 3_000, actual: 1_200, unspent: 1_800 },
];

const MOCK_RF_REVENUE_ROWS: RollForwardRevenueRow[] = [
  { id: "rf-milestone2", label: "Milestone 2", gridRowId: "design-signoff", forecast: 12_000, actualInvoiced: 0, uninvoiced: 12_000 },
];

// ─── Compute helpers ──────────────────────────────────────────────────────────

function sumMonths(values: Record<MonthKey, number>, months: MonthKey[]): number {
  return months.reduce((acc, m) => acc + (values[m] ?? 0), 0);
}

function computeCostActuals(groups: CostGroup[], actualMonths: MonthKey[]): number {
  return groups.reduce(
    (gs, g) => gs + g.rows.reduce((rs, r) => rs + sumMonths(r.values, actualMonths), 0),
    0,
  );
}

function computeCostFTC(groups: CostGroup[], futureMonths: MonthKey[]): number {
  return groups.reduce(
    (gs, g) => gs + g.rows.reduce((rs, r) => rs + sumMonths(r.values, futureMonths), 0),
    0,
  );
}

function computeCostBudget(groups: CostGroup[]): number {
  return groups.reduce((gs, g) => gs + g.rows.reduce((rs, r) => rs + r.budget, 0), 0);
}

function computeTmRevenueForecast(rows: TmRevenueRow[], months: MonthKey[]): number {
  return rows.reduce((sum, r) => sum + sumMonths(r.values, months), 0);
}

function computeFixedPriceRevenueForecast(milestones: MilestoneRow[]): number {
  return milestones
    .filter((m) => m.forecastDate !== null)
    .reduce((sum, m) => sum + m.amount, 0);
}

// ─── Shared metric card ───────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  tooltip?: string;
  accent?: "default" | "positive" | "negative" | "warning";
  className?: string;
}

function MetricCard({ label, value, icon, tooltip, accent = "default", className }: MetricCardProps) {
  const accentText = {
    default:  "text-foreground",
    positive: "text-emerald-600 dark:text-emerald-400",
    negative: "text-red-600 dark:text-red-400",
    warning:  "text-amber-600 dark:text-amber-400",
  }[accent];

  const iconBg = {
    default:  "bg-muted text-muted-foreground",
    positive: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400",
    negative: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400",
    warning:  "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400",
  }[accent];

  const card = (
    <Card className={cn("relative overflow-hidden", className)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-medium text-muted-foreground leading-tight">{label}</p>
          <div className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-md", iconBg)}>
            {icon}
          </div>
        </div>
        <div className={cn("mt-2 text-lg font-semibold leading-tight", accentText)}>{value}</div>
      </CardContent>
    </Card>
  );

  if (!tooltip) return card;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{card}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-56 text-xs">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function BudgetVarianceCard({ eac, totalBudget }: { eac: number; totalBudget: number }) {
  const variance  = eac - totalBudget;
  const isOver    = variance > 0;
  const isNeutral = variance === 0;
  const accent: MetricCardProps["accent"] = isNeutral ? "default" : isOver ? "negative" : "positive";
  const pct = totalBudget !== 0 ? Math.abs((variance / totalBudget) * 100) : 0;

  const value = (
    <div className="flex flex-col gap-0.5">
      <span>{isOver ? "+" : ""}{formatCurrency(variance)}</span>
      {!isNeutral && (
        <Badge
          variant="outline"
          className={cn(
            "w-fit text-[10px] px-1.5 py-0 font-normal",
            isOver
              ? "border-red-300 text-red-600 dark:border-red-800 dark:text-red-400"
              : "border-emerald-300 text-emerald-600 dark:border-emerald-800 dark:text-emerald-400",
          )}
        >
          {isOver ? "Over" : "Under"} by {pct.toFixed(1)}%
        </Badge>
      )}
    </div>
  );

  return (
    <MetricCard
      label="Budget Variance"
      value={value}
      icon={isOver ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
      accent={accent}
      tooltip="EAC minus Total Budget. Positive (red) = over budget; negative (green) = under budget."
    />
  );
}

function ForecastEndDateCard({ value, onChange, overrunRisk = false }: { value: string; onChange: (d: string) => void; overrunRisk?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);

  const commit = () => { if (draft) onChange(draft); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Card
          className="relative overflow-hidden cursor-pointer group"
          onClick={() => !editing && setEditing(true)}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-medium text-muted-foreground leading-tight">Forecast End Date</p>
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" />
              </div>
            </div>
            {editing ? (
              <div className="mt-2 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Input
                  type="date"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")  commit();
                    if (e.key === "Escape") cancel();
                  }}
                  className="h-7 text-sm px-2 py-0"
                  autoFocus
                />
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-emerald-600" onClick={commit}>
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-muted-foreground" onClick={cancel}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <div className="mt-2 flex flex-col gap-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-lg font-semibold leading-tight">{formatDate(value)}</span>
                  <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {overrunRisk && (
                  <Badge
                    variant="outline"
                    className="w-fit text-[10px] px-1.5 py-0 font-medium border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-400"
                  >
                    ⚠ Overrun risk
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-56 text-xs">
        Click to update the forecast end date. This is the PM's best estimate of when the project will complete.
      </TooltipContent>
    </Tooltip>
  );
}

// ─── Override Last Actuals Month Modal ───────────────────────────────────────

interface OverrideModalProps {
  open: boolean;
  currentMonth: MonthKey;
  draftMonth: MonthKey;
  onDraftChange: (mk: MonthKey) => void;
  onApply: () => void;
  onCancel: () => void;
}

function OverrideLastActualsMonthModal({
  open, currentMonth, draftMonth, onDraftChange, onApply, onCancel,
}: OverrideModalProps) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Override Last Actuals Month</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">
              Currently set to <strong>{formatMonthLabel(currentMonth)}</strong>
            </p>
            <Input
              type="month"
              value={draftMonth}
              onChange={(e) => onDraftChange(e.target.value)}
              className="text-sm"
            />
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <AlertTriangle className="mb-0.5 mr-1 inline h-3.5 w-3.5" />
            This overrides the global period close for this project only. Use only when actuals
            for this project are not yet finalised.
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onApply} disabled={!draftMonth}>Apply Override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Roll-Forward Proposal Modal ──────────────────────────────────────────────

interface RollForwardModalProps {
  open: boolean;
  closingMonth: MonthKey;
  nextMonth: MonthKey;
  costRows: RollForwardCostRow[];
  revenueRows: RollForwardRevenueRow[];
  costActions: Record<string, RollForwardRowState>;
  revenueActions: Record<string, RollForwardRowState>;
  onCostActionChange: (id: string, field: keyof RollForwardRowState, value: string) => void;
  onRevenueActionChange: (id: string, field: keyof RollForwardRowState, value: string) => void;
  onApply: () => void;
  onCancel: () => void;
}

function RollForwardModal({
  open, closingMonth, nextMonth, costRows, revenueRows,
  costActions, revenueActions, onCostActionChange, onRevenueActionChange, onApply, onCancel,
}: RollForwardModalProps) {
  const closingLabel = formatMonthLabel(closingMonth);

  const Th = ({ children, right }: { children: React.ReactNode; right?: boolean }) => (
    <th className={cn("px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/40 border-b", right ? "text-right" : "text-left")}>
      {children}
    </th>
  );

  const SectionHeader = ({ label, icon }: { label: string; icon: React.ReactNode }) => (
    <tr>
      <td colSpan={5} className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-foreground bg-muted/60 border-b border-t">
        <div className="flex items-center gap-1.5">{icon}{label}</div>
      </td>
    </tr>
  );

  const renderCostRow = (row: RollForwardCostRow) => {
    const state = costActions[row.id];
    if (!state) return null;
    return (
      <tr key={row.id} className="border-b hover:bg-muted/5 transition-colors">
        <td className="px-3 py-2.5 text-sm font-medium text-foreground">{row.label}</td>
        <td className="px-3 py-2.5 text-right text-sm tabular-nums text-muted-foreground">{formatCurrency(row.forecast)}</td>
        <td className="px-3 py-2.5 text-right text-sm tabular-nums text-muted-foreground">{formatCurrency(row.actual)}</td>
        <td className="px-3 py-2.5 text-right text-sm tabular-nums font-semibold text-amber-600 dark:text-amber-400">{formatCurrency(row.unspent)}</td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Select value={state.action} onValueChange={(v) => onCostActionChange(row.id, "action", v)}>
              <SelectTrigger className="h-7 w-[130px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Confirm">Confirm</SelectItem>
                <SelectItem value="Zero">Zero</SelectItem>
                <SelectItem value="Redistribute">Redistribute</SelectItem>
              </SelectContent>
            </Select>
            {state.action !== "Zero" ? (
              <Input type="month" value={state.moveTo} onChange={(e) => onCostActionChange(row.id, "moveTo", e.target.value)} className="h-7 w-[130px] text-xs px-2" />
            ) : (
              <span className="text-xs text-muted-foreground italic">No move</span>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderRevenueRow = (row: RollForwardRevenueRow) => {
    const state = revenueActions[row.id];
    if (!state) return null;
    return (
      <tr key={row.id} className="border-b hover:bg-muted/5 transition-colors">
        <td className="px-3 py-2.5 text-sm font-medium text-foreground">{row.label}</td>
        <td className="px-3 py-2.5 text-right text-sm tabular-nums text-muted-foreground">{formatCurrency(row.forecast)}</td>
        <td className="px-3 py-2.5 text-right text-sm tabular-nums text-muted-foreground">{formatCurrency(row.actualInvoiced)}</td>
        <td className="px-3 py-2.5 text-right text-sm tabular-nums font-semibold text-amber-600 dark:text-amber-400">{formatCurrency(row.uninvoiced)}</td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Select value={state.action} onValueChange={(v) => onRevenueActionChange(row.id, "action", v)}>
              <SelectTrigger className="h-7 w-[130px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Confirm">Confirm</SelectItem>
                <SelectItem value="Zero">Zero</SelectItem>
                <SelectItem value="Redistribute">Redistribute</SelectItem>
              </SelectContent>
            </Select>
            {state.action !== "Zero" ? (
              <Input type="month" value={state.moveTo} onChange={(e) => onRevenueActionChange(row.id, "moveTo", e.target.value)} className="h-7 w-[130px] text-xs px-2" />
            ) : (
              <span className="text-xs text-muted-foreground italic">No move</span>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-4xl w-full">
        <DialogHeader className="pb-2 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <LockOpen className="h-4 w-4 text-muted-foreground" />
            Roll-Forward Proposal — {closingLabel} Close
          </DialogTitle>
          <DialogDescription className="text-xs mt-1">
            Access Financials has closed <strong>{closingLabel}</strong>. Review unspent amounts
            below before they are carried forward.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-auto max-h-[55vh]">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr>
                <Th>Category</Th>
                <Th right>Forecast</Th>
                <Th right>Actual</Th>
                <Th right>Unspent</Th>
                <Th>Action &amp; Move to</Th>
              </tr>
            </thead>
            <tbody>
              <SectionHeader label="Costs" icon={<BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />} />
              {costRows.map(renderCostRow)}
              <SectionHeader label="Revenue" icon={<TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />} />
              {revenueRows.map(renderRevenueRow)}
            </tbody>
          </table>
        </div>
        <DialogFooter className="flex-row items-center justify-between gap-2 pt-3 border-t">
          <p className="text-xs text-muted-foreground">Rows where forecast = actual are hidden</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button onClick={onApply} className="bg-emerald-600 hover:bg-emerald-700 text-white">Apply</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cost Forecast Grid ───────────────────────────────────────────────────────

const ROW1_HEIGHT = "top-0";
const ROW2_TOP    = "top-[37px]";

type SummaryGridRow = {
  label: string;
  /** value per month — undefined means show "—" */
  values: Partial<Record<MonthKey, number>>;
  total: number | null;
  /** extra tailwind classes applied to every value cell */
  valueClass?: string;
  /** if true, future months always show "—" regardless of values */
  futureDash?: boolean;
};

function CostForecastGrid({
  groups, months, lastActualsMonth, onUpdateCell,
  categoryLabel = "Cost Category", totalLabel = "Total",
  summaryRows,
  htcRows,
}: {
  groups: CostGroup[];
  months: MonthKey[];
  lastActualsMonth: MonthKey;
  onUpdateCell: (rowId: string, month: MonthKey, value: number) => void;
  categoryLabel?: string;
  totalLabel?: string;
  summaryRows?: SummaryGridRow[];
  /** HTC row data — when provided, shows "Hrs remaining: N" beneath matching row labels */
  htcRows?: Array<{ label: string; hoursRemaining: number | null }>;
}) {
  const actualMonths = months.filter((m) => m <= lastActualsMonth);
  const futureMonths = months.filter((m) => m >  lastActualsMonth);
  const firstFuture  = futureMonths[0];

  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue,   setEditValue]   = useState("");
  const [collapsed,   setCollapsed]   = useState<Set<string>>(new Set());

  const startEdit = useCallback((rowId: string, month: MonthKey, current: number) => {
    setEditingCell({ rowId, month });
    setEditValue(current === 0 ? "" : String(current));
  }, []);

  const commitEdit = useCallback(() => {
    if (!editingCell) return;
    onUpdateCell(editingCell.rowId, editingCell.month, Number.parseFloat(editValue) || 0);
    setEditingCell(null);
    setEditValue("");
  }, [editingCell, editValue, onUpdateCell]);

  const cancelEdit = useCallback(() => { setEditingCell(null); setEditValue(""); }, []);

  const toggleGroup = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const rowEAC = (row: CostRow) => months.reduce((s, m) => s + (row.values[m] ?? 0), 0);
  const colSum = (ms: MonthKey[]) =>
    groups.reduce((gs, g) => gs + g.rows.reduce((rs, r) => rs + sumMonths(r.values, ms), 0), 0);

  const grandBudget   = computeCostBudget(groups);
  const grandEAC      = colSum(months);
  const grandVariance = grandEAC - grandBudget;

  // Sticky column class helpers — left group
  const thBudgetH   = "sticky left-[190px] z-20 bg-card px-2 py-2 text-right text-xs font-medium text-muted-foreground border-r whitespace-nowrap [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]";
  // Sticky column class helpers — right group
  const thTotalH    = "sticky right-[90px] z-20 bg-card px-2 py-2 text-right text-xs font-medium text-muted-foreground border-r whitespace-nowrap [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]";
  const thVarianceH = "sticky right-0 z-20 bg-card px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap min-w-[90px]";
  const thMonth = "px-2 py-1.5 text-right text-xs font-medium border-r min-w-[84px] whitespace-nowrap";

  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="flex flex-wrap items-center gap-4 px-3 py-2 border-b bg-muted/20 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded bg-muted/60 border" />
          <Lock className="h-2.5 w-2.5" />
          Locked actuals — read only
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded bg-background border border-primary/30" />
          <Pencil className="h-2.5 w-2.5 text-primary/70" />
          Forecast — click any cell to edit
        </div>
      </div>
      <div className="overflow-auto max-h-[620px]">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b">
              <th className={cn("sticky left-0 z-30 bg-card border-r min-w-[190px] w-[190px]", ROW1_HEIGHT)} />
              <th className={cn(thBudgetH, ROW1_HEIGHT, "min-w-[80px]")}>Budget</th>
              {actualMonths.length > 0 && (
                <th colSpan={actualMonths.length} className={cn("bg-muted/20 px-3 py-2 text-xs font-semibold text-muted-foreground border-r text-center", ROW1_HEIGHT)}>
                  <div className="flex items-center justify-center gap-1.5">
                    <Lock className="h-3 w-3" /> Actuals — locked
                  </div>
                </th>
              )}
              {futureMonths.length > 0 && (
                <th colSpan={futureMonths.length} className={cn("bg-background px-3 py-2 text-xs font-semibold text-primary/80 border-r text-center", ROW1_HEIGHT)}>
                  <div className="flex items-center justify-center gap-1.5">
                    <Pencil className="h-3 w-3" /> Forecast — editable
                  </div>
                </th>
              )}
              <th className={cn(thTotalH, ROW1_HEIGHT, "min-w-[90px]")}>Total EAC</th>
              <th className={cn(thVarianceH, ROW1_HEIGHT)}>Variance</th>
            </tr>
            <tr className="border-b">
              <th className={cn("sticky left-0 z-30 bg-card px-3 py-1.5 text-left text-xs font-semibold text-foreground border-r", ROW2_TOP)}>{categoryLabel}</th>
              <th className={cn("sticky left-[190px] z-20 bg-card border-r [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]", ROW2_TOP)} />
              {actualMonths.map((m) => (
                <th key={m} className={cn(thMonth, "bg-muted/20 text-muted-foreground", ROW2_TOP)}>
                  <div className="flex items-center justify-end gap-1">
                    <Lock className="h-2.5 w-2.5 shrink-0 opacity-50" />
                    {formatMonthLabel(m)}
                  </div>
                </th>
              ))}
              {futureMonths.map((m) => (
                <th key={m} className={cn(thMonth, "bg-background text-primary/70", ROW2_TOP, m === firstFuture && "border-l-2 border-l-primary/40")}>
                  {formatMonthLabel(m)}
                </th>
              ))}
              <th className={cn("sticky right-[90px] z-20 bg-card border-l-2 border-l-muted-foreground/20 border-r min-w-[90px] [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]", ROW2_TOP)} />
              <th className={cn("sticky right-0 z-20 bg-card min-w-[90px]", ROW2_TOP)} />
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const isCollapsed   = collapsed.has(group.id);
              const groupBudget   = group.rows.reduce((s, r) => s + r.budget, 0);
              const groupEAC      = group.rows.reduce((s, r) => s + rowEAC(r), 0);
              const groupVariance = groupEAC - groupBudget;
              return (
                <>
                  <tr key={`grp-${group.id}`} className="border-b bg-muted/25 cursor-pointer hover:bg-muted/40 transition-colors" onClick={() => toggleGroup(group.id)}>
                    <td className="sticky left-0 z-10 bg-card px-3 py-2 border-r font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <ChevronRight className={cn("h-3 w-3 shrink-0 transition-transform", !isCollapsed && "rotate-90")} />
                        {group.label}
                      </div>
                    </td>
                    <td className="sticky left-[190px] z-10 bg-card px-2 py-2 text-right text-xs font-medium text-muted-foreground border-r tabular-nums [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(groupBudget)}</td>
                    {actualMonths.map((m) => (
                      <td key={m} className="px-2 py-1.5 text-right text-xs border-r bg-muted/30 text-muted-foreground tabular-nums">{formatCurrency(group.rows.reduce((s, r) => s + (r.values[m] ?? 0), 0))}</td>
                    ))}
                    {futureMonths.map((m) => (
                      <td key={m} className={cn("px-2 py-2 text-right text-xs font-medium border-r tabular-nums", m === firstFuture && "border-l-2 border-l-primary/40")}>
                        {formatCurrency(group.rows.reduce((s, r) => s + (r.values[m] ?? 0), 0))}
                      </td>
                    ))}
                    <td className="sticky right-[90px] z-10 bg-card px-3 py-2 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(groupEAC)}</td>
                    <td className={cn("sticky right-0 z-10 bg-card px-3 py-2 text-right text-xs font-semibold tabular-nums", groupVariance < 0 ? "text-emerald-600 dark:text-emerald-400" : groupVariance > 0 ? "text-red-600 dark:text-red-400" : "text-muted-foreground")}>
                      {groupVariance === 0 ? "—" : `${groupVariance > 0 ? "+" : ""}${formatCurrency(groupVariance)}`}
                    </td>
                  </tr>
                  {!isCollapsed && group.rows.map((row) => {
                    const total    = rowEAC(row);
                    const variance = total - row.budget;
                    return (
                      <tr key={row.id} className="border-b hover:bg-muted/10 transition-colors">
                        <td className="sticky left-0 z-10 bg-background px-3 py-1.5 border-r text-xs text-muted-foreground pl-8">
                          <span>{row.label}</span>
                          {(() => {
                            if (!htcRows) return null;
                            const htcRow = htcRows.find((h) => h.label === row.label);
                            if (!htcRow || htcRow.hoursRemaining === null) return null;
                            return (
                              <span className={cn("block text-[11px] mt-0.5 font-normal", htcRow.hoursRemaining === 0 ? "text-amber-500 dark:text-amber-400" : "text-muted-foreground/70")}>
                                Hrs remaining: {htcRow.hoursRemaining}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="sticky left-[190px] z-10 bg-background px-2 py-1.5 text-right text-xs text-muted-foreground border-r tabular-nums [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(row.budget)}</td>
                        {actualMonths.map((m) => (
                          <td key={m} className="px-2 py-1.5 text-right text-xs border-r bg-muted/30 text-muted-foreground tabular-nums select-none">
                            {(row.values[m] ?? 0) > 0 ? formatCurrency(row.values[m] ?? 0) : <span className="opacity-25">—</span>}
                          </td>
                        ))}
                        {futureMonths.map((m) => {
                          const val       = row.values[m] ?? 0;
                          const isEditing = editingCell?.rowId === row.id && editingCell?.month === m;
                          return (
                            <td key={m} className={cn("px-2 py-1.5 text-right text-xs border-r cursor-text tabular-nums group/cell hover:bg-primary/5", m === firstFuture && "border-l-2 border-l-primary/40")} onClick={() => !isEditing && startEdit(row.id, m, val)}>
                              {isEditing ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={commitEdit} onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") cancelEdit(); }} className="w-full min-w-[60px] text-right bg-primary/5 border border-primary/40 rounded px-1 py-0 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary/30" autoFocus />
                              ) : (
                                <span className="group-hover/cell:text-primary">{val > 0 ? formatCurrency(val) : <span className="text-muted-foreground/30">—</span>}</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="sticky right-[90px] z-10 bg-background px-3 py-1.5 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(total)}</td>
                        <td className={cn("sticky right-0 z-10 bg-background px-3 py-1.5 text-right text-xs tabular-nums", variance < 0 ? "text-emerald-600 dark:text-emerald-400" : variance > 0 ? "text-red-600 dark:text-red-400" : "text-muted-foreground")}>
                          {variance === 0 ? "—" : `${variance > 0 ? "+" : ""}${formatCurrency(variance)}`}
                        </td>
                      </tr>
                    );
                  })}
                </>
              );
            })}
            <tr className="border-t-2 border-t-border bg-muted/40">
              <td className="sticky left-0 z-10 bg-card px-3 py-2.5 border-r text-xs font-bold text-foreground">{totalLabel}</td>
              <td className="sticky left-[190px] z-10 bg-card px-2 py-2.5 text-right text-xs font-bold border-r tabular-nums [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(grandBudget)}</td>
              {actualMonths.map((m) => (
                <td key={m} className="px-2 py-2.5 text-right text-xs font-semibold border-r bg-muted/30 tabular-nums">{formatCurrency(colSum([m]))}</td>
              ))}
              {futureMonths.map((m) => (
                <td key={m} className={cn("px-2 py-2.5 text-right text-xs font-semibold border-r tabular-nums", m === firstFuture && "border-l-2 border-l-primary/40")}>{formatCurrency(colSum([m]))}</td>
              ))}
              <td className="sticky right-[90px] z-10 bg-card px-3 py-2.5 text-right text-xs font-bold border-l-2 border-l-muted-foreground/20 border-r tabular-nums [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(grandEAC)}</td>
              <td className={cn("sticky right-0 z-10 bg-card px-3 py-2.5 text-right text-xs font-bold tabular-nums", grandVariance < 0 ? "text-emerald-600 dark:text-emerald-400" : grandVariance > 0 ? "text-red-600 dark:text-red-400" : "text-muted-foreground")}>
                {grandVariance === 0 ? "—" : `${grandVariance > 0 ? "+" : ""}${formatCurrency(grandVariance)}`}
              </td>
            </tr>

            {/* ── Optional summary rows (e.g. Actual Invoiced, WIP) ── */}
            {summaryRows && summaryRows.length > 0 && (
              <>
                <tr className="border-t border-t-border/60">
                  <td colSpan={months.length + 4} className="p-0 bg-muted/10" />
                </tr>
                {summaryRows.map((sr) => (
                  <tr key={sr.label} className="border-b last:border-b-0 bg-muted/5 hover:bg-muted/10 transition-colors">
                    <td className="sticky left-0 z-10 bg-card px-3 py-2 border-r text-xs font-medium text-muted-foreground whitespace-nowrap">
                      {sr.label}
                    </td>
                    {/* Budget column — blank for summary rows */}
                    <td className="sticky left-[190px] z-10 bg-card px-2 py-2 border-r text-muted-foreground/30 text-right text-xs tabular-nums [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]">—</td>
                    {actualMonths.map((m) => {
                      const v = sr.values[m];
                      return (
                        <td key={m} className={cn("px-2 py-2 text-right text-xs border-r tabular-nums bg-muted/20", sr.valueClass)}>
                          {v !== undefined ? formatCurrency(v) : <span className="opacity-30">—</span>}
                        </td>
                      );
                    })}
                    {futureMonths.map((m, i) => {
                      const v = !sr.futureDash ? sr.values[m] : undefined;
                      return (
                        <td key={m} className={cn("px-2 py-2 text-right text-xs border-r tabular-nums text-muted-foreground/40", i === 0 && "border-l-2 border-l-primary/40")}>
                          {v !== undefined ? formatCurrency(v) : <span className="opacity-30">—</span>}
                        </td>
                      );
                    })}
                    {/* Total EAC column */}
                    <td className="sticky right-[90px] z-10 bg-card px-3 py-2 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]">
                      {sr.total !== null ? formatCurrency(sr.total) : <span className="opacity-30">—</span>}
                    </td>
                    {/* Variance column — blank for summary rows */}
                    <td className="sticky right-0 z-10 bg-card px-3 py-2" />
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Revenue sub-tab: T&M category grid ──────────────────────────────────────

function TmRevenueCategoryGrid({
  rows, months, lastActualsMonth, actualInvoiced, onUpdateCell,
}: {
  rows: TmRevenueRow[];
  months: MonthKey[];
  lastActualsMonth: MonthKey;
  actualInvoiced: number;
  onUpdateCell: (rowId: string, month: MonthKey, value: number) => void;
}) {
  const actualMonths = months.filter((m) => m <= lastActualsMonth);
  const futureMonths = months.filter((m) => m >  lastActualsMonth);
  const firstFuture  = futureMonths[0];

  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue,   setEditValue]   = useState("");

  const startEdit = (rowId: string, month: MonthKey, current: number) => {
    setEditingCell({ rowId, month });
    setEditValue(current === 0 ? "" : String(current));
  };
  const commitEdit = () => {
    if (!editingCell) return;
    onUpdateCell(editingCell.rowId, editingCell.month, Number.parseFloat(editValue) || 0);
    setEditingCell(null);
    setEditValue("");
  };
  const cancelEdit = () => { setEditingCell(null); setEditValue(""); };

  const rowTotal    = (row: TmRevenueRow) => months.reduce((s, m) => s + (row.values[m] ?? 0), 0);
  const colSum      = (ms: MonthKey[])    => rows.reduce((rs, r) => rs + sumMonths(r.values, ms), 0);
  const grandBudget = rows.reduce((s, r) => s + r.budget, 0);
  const grandTotal  = colSum(months);
  const wip         = colSum(actualMonths) - actualInvoiced;

  // Sticky column class helpers — left group (label=220px)
  const thBudgetH   = "sticky left-[220px] z-20 bg-card px-2 py-2 text-right text-xs font-medium text-muted-foreground border-r whitespace-nowrap [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]";
  // Sticky column class helpers — right group
  const thTotalH    = "sticky right-[90px] z-20 bg-card px-2 py-2 text-right text-xs font-medium text-muted-foreground border-r whitespace-nowrap [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]";
  const thVarianceH = "sticky right-0 z-20 bg-card px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap min-w-[90px]";
  const thMonth = "px-2 py-1.5 text-right text-xs font-medium border-r min-w-[84px] whitespace-nowrap";

  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="flex flex-wrap items-center gap-4 px-3 py-2 border-b bg-muted/20 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded bg-muted/60 border" /><Lock className="h-2.5 w-2.5" />
          Locked actuals — read only
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded bg-background border border-primary/30" /><Pencil className="h-2.5 w-2.5 text-primary/70" />
          Forecast — click any cell to edit
        </div>
      </div>
      <div className="overflow-auto max-h-[620px]">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b">
              <th className={cn("sticky left-0 z-30 bg-card border-r min-w-[220px] w-[220px]", ROW1_HEIGHT)} />
              <th className={cn(thBudgetH, ROW1_HEIGHT, "min-w-[80px]")}>Budget</th>
              {actualMonths.length > 0 && (
                <th colSpan={actualMonths.length} className={cn("bg-muted/20 px-3 py-2 text-xs font-semibold text-muted-foreground border-r text-center", ROW1_HEIGHT)}>
                  <div className="flex items-center justify-center gap-1.5"><Lock className="h-3 w-3" /> Actuals — locked</div>
                </th>
              )}
              {futureMonths.length > 0 && (
                <th colSpan={futureMonths.length} className={cn("bg-background px-3 py-2 text-xs font-semibold text-primary/80 border-r text-center", ROW1_HEIGHT)}>
                  <div className="flex items-center justify-center gap-1.5"><Pencil className="h-3 w-3" /> Forecast — editable</div>
                </th>
              )}
              <th className={cn(thTotalH, ROW1_HEIGHT, "min-w-[90px]")}>Total</th>
              <th className={cn(thVarianceH, ROW1_HEIGHT)}>Variance</th>
            </tr>
            <tr className="border-b">
              <th className={cn("sticky left-0 z-30 bg-card px-3 py-1.5 text-left text-xs font-semibold text-foreground border-r", ROW2_TOP)}>Revenue Category</th>
              <th className={cn("sticky left-[220px] z-20 bg-card border-r [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]", ROW2_TOP)} />
              {actualMonths.map((m) => (
                <th key={m} className={cn(thMonth, "bg-muted/20 text-muted-foreground", ROW2_TOP)}>
                  <div className="flex items-center justify-end gap-1"><Lock className="h-2.5 w-2.5 shrink-0 opacity-50" />{formatMonthLabel(m)}</div>
                </th>
              ))}
              {futureMonths.map((m) => (
                <th key={m} className={cn(thMonth, "bg-background text-primary/70", ROW2_TOP, m === firstFuture && "border-l-2 border-l-primary/40")}>
                  {formatMonthLabel(m)}
                </th>
              ))}
              <th className={cn("sticky right-[90px] z-20 bg-card border-l-2 border-l-muted-foreground/20 border-r min-w-[90px] [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]", ROW2_TOP)} />
              <th className={cn("sticky right-0 z-20 bg-card min-w-[90px]", ROW2_TOP)} />
            </tr>
          </thead>
          <tbody>
            {/* Forecast revenue rows */}
            {rows.map((row) => {
              const total    = rowTotal(row);
              const variance = total - row.budget;
              return (
                <tr key={row.id} className="border-b hover:bg-muted/10 transition-colors">
                  <td className="sticky left-0 z-10 bg-background px-3 py-2 border-r text-xs font-medium text-foreground">{row.label}</td>
                  <td className="sticky left-[220px] z-10 bg-background px-2 py-2 text-right text-xs text-muted-foreground border-r tabular-nums [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(row.budget)}</td>
                  {actualMonths.map((m) => {
                    const val = row.values[m] ?? 0;
                    return (
                      <td key={m} className="px-2 py-1.5 text-right text-xs border-r bg-muted/30 tabular-nums select-none">
                        {val > 0 ? <span className="text-emerald-700 dark:text-emerald-400">{formatCurrency(val)}</span> : <span className="opacity-25">—</span>}
                      </td>
                    );
                  })}
                  {futureMonths.map((m) => {
                    const val       = row.values[m] ?? 0;
                    const isEditing = editingCell?.rowId === row.id && editingCell?.month === m;
                    return (
                      <td key={m} className={cn("px-2 py-1.5 text-right text-xs border-r cursor-text tabular-nums group/cell hover:bg-primary/5", m === firstFuture && "border-l-2 border-l-primary/40")} onClick={() => !isEditing && startEdit(row.id, m, val)}>
                        {isEditing ? (
                          <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={commitEdit} onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") cancelEdit(); }} className="w-full min-w-[60px] text-right bg-primary/5 border border-primary/40 rounded px-1 py-0 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary/30" autoFocus />
                        ) : (
                          <span className="group-hover/cell:text-primary">
                            {val > 0 ? <span className="text-emerald-700 dark:text-emerald-400">{formatCurrency(val)}</span> : <span className="text-muted-foreground/30">—</span>}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="sticky right-[90px] z-10 bg-background px-3 py-2 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums text-emerald-700 dark:text-emerald-400 [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(total)}</td>
                  <td className={cn("sticky right-0 z-10 bg-background px-3 py-2 text-right text-xs tabular-nums", variance < 0 ? "text-red-600 dark:text-red-400" : variance > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")}>
                    {variance === 0 ? "—" : `${variance > 0 ? "+" : ""}${formatCurrency(variance)}`}
                  </td>
                </tr>
              );
            })}

            {/* Total Forecast Revenue */}
            <tr className="border-t-2 border-t-border bg-muted/40">
              <td className="sticky left-0 z-10 bg-card px-3 py-2.5 border-r text-xs font-bold text-foreground">Total Forecast Revenue</td>
              <td className="sticky left-[220px] z-10 bg-card px-2 py-2.5 text-right text-xs font-bold border-r tabular-nums [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(grandBudget)}</td>
              {actualMonths.map((m) => (
                <td key={m} className="px-2 py-2.5 text-right text-xs font-semibold border-r bg-muted/30 tabular-nums text-emerald-700 dark:text-emerald-400">{formatCurrency(colSum([m]))}</td>
              ))}
              {futureMonths.map((m) => (
                <td key={m} className={cn("px-2 py-2.5 text-right text-xs font-semibold border-r tabular-nums text-emerald-700 dark:text-emerald-400", m === firstFuture && "border-l-2 border-l-primary/40")}>{formatCurrency(colSum([m]))}</td>
              ))}
              <td className="sticky right-[90px] z-10 bg-card px-3 py-2.5 text-right text-xs font-bold border-l-2 border-l-muted-foreground/20 border-r tabular-nums text-emerald-700 dark:text-emerald-400 [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(grandTotal)}</td>
              <td className="sticky right-0 z-10 bg-card" />
            </tr>

            {/* Actual Invoiced — read-only, all locked */}
            <tr className="border-b bg-muted/10">
              <td className="sticky left-0 z-10 bg-card px-3 py-2 border-r text-xs font-medium text-muted-foreground">Actual Invoiced <span className="text-[10px] ml-1 opacity-60">(from FINS)</span></td>
              <td className="sticky left-[220px] z-10 bg-card px-2 py-2 border-r [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]" />
              {actualMonths.map((m, i) => (
                <td key={m} className="px-2 py-2 text-right text-xs border-r bg-muted/30 tabular-nums text-muted-foreground">
                  {/* Distribute mock actual invoiced across actual months evenly */}
                  {formatCurrency(Math.round(actualInvoiced / Math.max(actualMonths.length, 1)))}
                  {i === actualMonths.length - 1 && <span className="sr-only">total so far</span>}
                </td>
              ))}
              {futureMonths.map((m) => (
                <td key={m} className={cn("px-2 py-2 border-r bg-muted/5", m === firstFuture && "border-l-2 border-l-primary/40")} />
              ))}
              <td className="sticky right-[90px] z-10 bg-card px-3 py-2 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums text-muted-foreground [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]">{formatCurrency(actualInvoiced)}</td>
              <td className="sticky right-0 z-10 bg-card" />
            </tr>

            {/* Revenue Variance */}
            <tr className="border-b bg-muted/5">
              <td className="sticky left-0 z-10 bg-card px-3 py-2 border-r text-xs font-medium text-muted-foreground">Revenue Variance</td>
              <td className="sticky left-[220px] z-10 bg-card px-2 py-2 border-r [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]" />
              {months.map((m) => <td key={m} className="px-2 py-2 border-r" />)}
              <td className={cn("sticky right-[90px] z-10 bg-card px-3 py-2 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]", (grandTotal - actualInvoiced) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>
                {formatCurrency(grandTotal - actualInvoiced)}
              </td>
              <td className="sticky right-0 z-10 bg-card" />
            </tr>

            {/* WIP */}
            <tr className="border-b bg-muted/5">
              <td className="sticky left-0 z-10 bg-card px-3 py-2 border-r text-xs font-medium text-muted-foreground">
                WIP <span className="text-[10px] ml-1 opacity-60">(uninvoiced earned revenue)</span>
              </td>
              <td className="sticky left-[220px] z-10 bg-card px-2 py-2 border-r [box-shadow:2px_0_4px_rgba(0,0,0,0.08)]" />
              {months.map((m) => <td key={m} className="px-2 py-2 border-r" />)}
              <td className={cn("sticky right-[90px] z-10 bg-card px-3 py-2 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums [box-shadow:-2px_0_4px_rgba(0,0,0,0.08)]", wip >= 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground")}>
                {formatCurrency(Math.max(0, wip))}
              </td>
              <td className="sticky right-0 z-10 bg-card" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Revenue sub-tab: Fixed-price milestone grid ──────────────────────────────

function FixedPriceMilestoneGrid({
  milestones, months, lastActualsMonth, actualInvoiced, onUpdateMilestone,
}: {
  milestones: MilestoneRow[];
  months: MonthKey[];
  lastActualsMonth: MonthKey;
  actualInvoiced: number;
  onUpdateMilestone: (id: string, forecastDate: MonthKey | null) => void;
}) {
  const actualMonths    = months.filter((m) => m <= lastActualsMonth);
  const futureMonths    = months.filter((m) => m >  lastActualsMonth);
  const firstFuture     = futureMonths[0];

  // Inline forecast-date editing state
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [draftDate,     setDraftDate]     = useState<string>("");

  const startDateEdit = (id: string, current: MonthKey | null) => {
    setEditingDateId(id);
    setDraftDate(current ?? "");
  };
  const commitDateEdit = (id: string) => {
    onUpdateMilestone(id, draftDate || null);
    setEditingDateId(null);
  };
  const cancelDateEdit = () => setEditingDateId(null);

  const unscheduled      = milestones.filter((m) => m.forecastDate === null);
  const unscheduledTotal = unscheduled.reduce((s, m) => s + m.amount, 0);
  const scheduledTotal   = milestones.filter((m) => m.forecastDate !== null).reduce((s, m) => s + m.amount, 0);
  const contractTotal    = milestones.reduce((s, m) => s + m.contractValue, 0);

  const thBase = "px-3 py-2 text-xs font-semibold text-muted-foreground border-b border-r bg-muted/30 whitespace-nowrap";
  const thMonth = "px-2 py-1.5 text-right text-xs font-medium border-r min-w-[84px] whitespace-nowrap";

  return (
    <div className="space-y-3">
      <div className="rounded-lg border overflow-hidden">
        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 px-3 py-2 border-b bg-muted/20 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Pencil className="h-2.5 w-2.5 text-primary/70" />
            Click the Forecast Date column to set or update a milestone's expected invoice month
          </div>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300">Unscheduled</Badge>
            No forecast date — amount excluded from monthly view
          </div>
        </div>

        <div className="overflow-auto max-h-[620px]">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              {/* Row 1: section spans */}
              <tr className="border-b">
                <th className={cn("sticky left-0 z-30 min-w-[200px] w-[200px]", thBase, ROW1_HEIGHT)}>Milestone</th>
                <th className={cn(thBase, ROW1_HEIGHT, "min-w-[130px]")}>Forecast Date</th>
                {actualMonths.length > 0 && (
                  <th colSpan={actualMonths.length} className={cn("sticky z-20 bg-muted/20 px-3 py-2 text-xs font-semibold text-muted-foreground border-r text-center", ROW1_HEIGHT)}>
                    <div className="flex items-center justify-center gap-1.5"><Lock className="h-3 w-3" /> Actuals — locked</div>
                  </th>
                )}
                {futureMonths.length > 0 && (
                  <th colSpan={futureMonths.length} className={cn("sticky z-20 bg-background px-3 py-2 text-xs font-semibold text-primary/80 border-r text-center", ROW1_HEIGHT)}>
                    <div className="flex items-center justify-center gap-1.5"><Pencil className="h-3 w-3" /> Forecast — editable</div>
                  </th>
                )}
                <th className={cn(thBase, ROW1_HEIGHT, "min-w-[90px] text-right")}>Total</th>
                <th className={cn(thBase, ROW1_HEIGHT, "min-w-[110px] text-right")}>Contract Value</th>
              </tr>
              {/* Row 2: month labels */}
              <tr className="border-b">
                <th className={cn("sticky left-0 z-30 bg-card px-3 py-1.5 text-left text-xs font-semibold text-foreground border-r", ROW2_TOP)} />
                <th className={cn("sticky z-20 bg-muted/10 border-r px-3 py-1.5 text-xs text-muted-foreground", ROW2_TOP)}>PM's expected date</th>
                {actualMonths.map((m) => (
                  <th key={m} className={cn(thMonth, "sticky z-20 bg-muted/20 text-muted-foreground", ROW2_TOP)}>
                    <div className="flex items-center justify-end gap-1"><Lock className="h-2.5 w-2.5 shrink-0 opacity-50" />{formatMonthLabel(m)}</div>
                  </th>
                ))}
                {futureMonths.map((m) => (
                  <th key={m} className={cn(thMonth, "sticky z-20 bg-background text-primary/70", ROW2_TOP, m === firstFuture && "border-l-2 border-l-primary/40")}>
                    {formatMonthLabel(m)}
                  </th>
                ))}
                <th className={cn("sticky z-20 bg-muted/10 border-l-2 border-l-muted-foreground/20 border-r min-w-[90px]", ROW2_TOP)} />
                <th className={cn("sticky z-20 bg-muted/10 min-w-[110px]", ROW2_TOP)} />
              </tr>
            </thead>
            <tbody>
              {milestones.map((milestone) => {
                const isEditing   = editingDateId === milestone.id;
                const isScheduled = milestone.forecastDate !== null;

                return (
                  <tr key={milestone.id} className="border-b hover:bg-muted/10 transition-colors">
                    {/* Name */}
                    <td className="sticky left-0 z-10 bg-background px-3 py-2.5 border-r">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-foreground">{milestone.name}</span>
                        {!isScheduled && (
                          <Badge variant="outline" className="w-fit text-[10px] px-1.5 py-0 font-normal border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300">
                            Unscheduled
                          </Badge>
                        )}
                      </div>
                    </td>

                    {/* Forecast Date — editable */}
                    <td
                      className="px-3 py-2.5 border-r cursor-pointer hover:bg-primary/5 group/date"
                      onClick={() => !isEditing && startDateEdit(milestone.id, milestone.forecastDate)}
                    >
                      {isEditing ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Input
                            type="month"
                            value={draftDate}
                            onChange={(e) => setDraftDate(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter")  commitDateEdit(milestone.id);
                              if (e.key === "Escape") cancelDateEdit();
                            }}
                            className="h-7 text-xs px-2 w-[120px]"
                            autoFocus
                          />
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-600 shrink-0" onClick={() => commitDateEdit(milestone.id)}>
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground shrink-0" onClick={cancelDateEdit}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          {isScheduled ? (
                            <span className="text-xs text-foreground font-medium">{formatMonthLabel(milestone.forecastDate!)}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground/50 italic">Not set</span>
                          )}
                          <Pencil className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover/date:opacity-100 transition-opacity" />
                        </div>
                      )}
                    </td>

                    {/* Month columns */}
                    {actualMonths.map((m) => {
                      const hasAmount = isScheduled && milestone.forecastDate === m;
                      return (
                        <td key={m} className="px-2 py-2.5 text-right text-xs border-r bg-muted/30 tabular-nums select-none">
                          {hasAmount
                            ? <span className="text-emerald-700 dark:text-emerald-400 font-medium">{formatCurrency(milestone.amount)}</span>
                            : <span className="opacity-20">—</span>}
                        </td>
                      );
                    })}
                    {futureMonths.map((m) => {
                      const hasAmount = isScheduled && milestone.forecastDate === m;
                      return (
                        <td key={m} className={cn("px-2 py-2.5 text-right text-xs border-r tabular-nums", m === firstFuture && "border-l-2 border-l-primary/40")}>
                          {hasAmount
                            ? <span className="text-emerald-700 dark:text-emerald-400 font-medium">{formatCurrency(milestone.amount)}</span>
                            : <span className="opacity-20">—</span>}
                        </td>
                      );
                    })}

                    {/* Total */}
                    <td className="px-3 py-2.5 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums">
                      {isScheduled
                        ? <span className="text-emerald-700 dark:text-emerald-400">{formatCurrency(milestone.amount)}</span>
                        : <span className="text-muted-foreground/40">—</span>}
                    </td>

                    {/* Contract Value */}
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted-foreground">
                      {formatCurrency(milestone.contractValue)}
                    </td>
                  </tr>
                );
              })}

              {/* Total Forecast Revenue row */}
              <tr className="border-t-2 border-t-border bg-muted/40">
                <td className="sticky left-0 z-10 bg-card px-3 py-2.5 border-r text-xs font-bold text-foreground">Total Forecast Revenue</td>
                <td className="px-3 py-2.5 border-r" />
                {actualMonths.map((m) => {
                  const amt = milestones.filter(ms => ms.forecastDate === m && ms.forecastDate !== null).reduce((s, ms) => s + ms.amount, 0);
                  return (
                    <td key={m} className="px-2 py-2.5 text-right text-xs font-semibold border-r bg-muted/30 tabular-nums text-emerald-700 dark:text-emerald-400">
                      {amt > 0 ? formatCurrency(amt) : <span className="text-muted-foreground/30">—</span>}
                    </td>
                  );
                })}
                {futureMonths.map((m) => {
                  const amt = milestones.filter(ms => ms.forecastDate === m && ms.forecastDate !== null).reduce((s, ms) => s + ms.amount, 0);
                  return (
                    <td key={m} className={cn("px-2 py-2.5 text-right text-xs font-semibold border-r tabular-nums text-emerald-700 dark:text-emerald-400", m === firstFuture && "border-l-2 border-l-primary/40")}>
                      {amt > 0 ? formatCurrency(amt) : <span className="text-muted-foreground/30">—</span>}
                    </td>
                  );
                })}
                <td className="px-3 py-2.5 text-right text-xs font-bold border-l-2 border-l-muted-foreground/20 border-r tabular-nums text-emerald-700 dark:text-emerald-400">
                  {formatCurrency(scheduledTotal)}
                </td>
                <td className="px-3 py-2.5 text-right text-xs font-bold tabular-nums text-muted-foreground">
                  {formatCurrency(contractTotal)}
                </td>
              </tr>

              {/* Actual Invoiced */}
              <tr className="border-b bg-muted/10">
                <td className="sticky left-0 z-10 bg-card px-3 py-2 border-r text-xs font-medium text-muted-foreground">
                  Actual Invoiced <span className="text-[10px] ml-1 opacity-60">(from FINS)</span>
                </td>
                <td className="px-3 py-2 border-r" />
                {months.map((m) => <td key={m} className="px-2 py-2 border-r bg-muted/30 text-right text-xs text-muted-foreground">—</td>)}
                <td className="px-3 py-2 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums text-muted-foreground">
                  {formatCurrency(actualInvoiced)}
                </td>
                <td />
              </tr>

              {/* Revenue Variance */}
              <tr className="border-b bg-muted/5">
                <td className="sticky left-0 z-10 bg-card px-3 py-2 border-r text-xs font-medium text-muted-foreground">Revenue Variance</td>
                <td className="px-3 py-2 border-r" />
                {months.map((m) => <td key={m} className="px-2 py-2 border-r" />)}
                <td className={cn("px-3 py-2 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums", (scheduledTotal - actualInvoiced) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>
                  {formatCurrency(scheduledTotal - actualInvoiced)}
                </td>
                <td />
              </tr>

              {/* WIP */}
              <tr className="border-b bg-muted/5">
                <td className="sticky left-0 z-10 bg-card px-3 py-2 border-r text-xs font-medium text-muted-foreground">
                  WIP <span className="text-[10px] ml-1 opacity-60">(uninvoiced earned revenue)</span>
                </td>
                <td className="px-3 py-2 border-r" />
                {months.map((m) => <td key={m} className="px-2 py-2 border-r" />)}
                <td className="px-3 py-2 text-right text-xs font-semibold border-l-2 border-l-muted-foreground/20 border-r tabular-nums text-amber-600 dark:text-amber-400">
                  {formatCurrency(Math.max(0, scheduledTotal - actualInvoiced))}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Unscheduled warning note */}
      {unscheduledTotal > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>
            <strong>{formatCurrency(unscheduledTotal)}</strong> unscheduled
            {unscheduled.length === 1 ? ` (${unscheduled[0]?.name})` : ` across ${unscheduled.length} milestones`}
            {" — assign a forecast date to include in the monthly view."}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Revenue sub-tab: Retainer placeholder ────────────────────────────────────

function RetainerPlaceholder() {
  return (
    <div className="rounded-lg border border-dashed bg-muted/10 px-6 py-14 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/40">
        <Clock className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground">Revenue forecasting for Retainer projects is coming soon.</p>
      <p className="mt-1 text-xs text-muted-foreground">
        T&amp;M and Fixed Price project types are fully supported. Retainer support is planned for a future release.
      </p>
    </div>
  );
}

// ─── ForecastTab (main export) ────────────────────────────────────────────────

// ── Permission gate note ──────────────────────────────────────────────────────
// The "Override" link (Last Actuals Month) should only be visible to Finance
// User and Administrator roles (spec §9.4: forecastLastActualsOverride).
// When the permissions context is wired up, wrap with:
//   {permissions.projectActions?.forecastLastActualsOverride && <button…>Override</button>}
// For now it is always visible (mock / prototype mode).

// ─── Roll-Forward Proposal — mock state ────────────────────────────────────────
// Mock — dev team will replace this with a real API call to
// GET /projects/{id}/forecast/roll-forward-proposal
// Shape matches ProjectForecastOverride status in the spec (§6).
type RollForwardProposal = {
  status: "pending" | "pm-reviewed" | "applied" | "cancelled";
  periodClosed: string; // "YYYY-MM"
  proposedBy: string;
  proposedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
};

// Mock — dev team will replace with API call to
// GET /projects/{id}/forecast/roll-forward-proposal
const mockPendingProposal: RollForwardProposal = {
  // Default to 'pm-reviewed' so Stage 3 (Finance Resolve) is shown immediately.
  // Change to 'pending' to preview Stage 1 (Finance Propose).
  status: "pm-reviewed",
  periodClosed: "2027-01", // renders as "January 2027"
  proposedBy: "Finance Team",
  proposedAt: "2027-02-01",
  reviewedBy: "PM User",
  reviewedAt: "2026-09-18",
};

// ─── RollForwardBanner ─────────────────────────────────────────────────────────
function RollForwardBanner({
  proposal,
  onReview,
}: {
  proposal: RollForwardProposal | null;
  onReview: () => void;
}) {
  if (!proposal) return null;
  if (proposal.status === "applied" || proposal.status === "cancelled") return null;

  // Format "YYYY-MM" → "January 2027"
  const [year, month] = proposal.periodClosed.split("-").map(Number);
  const periodLabel = new Date(year, month - 1, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  // PM has responded — show a blue informational banner
  if (proposal.status === "pm-reviewed") {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 dark:border-blue-700 dark:bg-blue-950/30">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
        <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
              Roll-Forward response submitted — awaiting Finance resolution
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-400">
              The PM has reviewed the proposal for{" "}
              <span className="font-medium">{periodLabel}</span>. Finance can
              now review the responses and apply the roll-forward.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 border-blue-400 bg-white text-blue-800 hover:bg-blue-100 dark:border-blue-600 dark:bg-transparent dark:text-blue-300 dark:hover:bg-blue-900/40"
            onClick={onReview}
          >
            View Proposal →
          </Button>
        </div>
      </div>
    );
  }

  // Default — pending PM review
  return (
    <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-700 dark:bg-amber-950/30">
      <Bell className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            A Roll-Forward Proposal is pending your review
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Finance has proposed moving unspent costs from{" "}
            <span className="font-medium">{periodLabel}</span>. Review and
            accept or challenge each line.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-amber-400 bg-white text-amber-800 hover:bg-amber-100 dark:border-amber-600 dark:bg-transparent dark:text-amber-300 dark:hover:bg-amber-900/40"
          onClick={onReview}
        >
          Review Roll-Forward →
        </Button>
      </div>
    </div>
  );
}

// ─── RollForwardModalShell ──────────────────────────────────────────────────
// Modal shell for the Roll-Forward review flow.
// Content (step detail tables) is added in subsequent prompts.
// Stage mapping:
//   'pending'     → Step 1 (Finance Proposes) active
//   'pm-reviewed' → Step 2 (PM Reviews) active
//   'applied'     → Step 3 (Finance Applies) active

// ── Proposal line type ───────────────────────────────────────────────────────
// In production, populated from GET /projects/{id}/forecast/roll-forward-proposal
interface ProposalLine {
  id: number;
  rowLabel: string;    // cost category / cost centre name
  rowId: string;       // empty string = user-added row (free-text label)
  fromPeriod: string;  // YYYY-MM — the period being closed
  unspentAmount: number;
  toPeriod: string;    // YYYY-MM — target month for the moved amount
  amount: number;
  // PM review fields — null until PM responds
  pmResponse: null | "accepted" | "challenged";
  pmComment: string;   // required when pmResponse === 'challenged'
  // Finance resolution fields — populated in Stage 3
  financeResolution: null | "proceed" | "revised";
  financeNote: string;          // required when financeResolution === 'proceed'
  revisedToPeriod: string | null; // set when financeResolution === 'revised'
  revisedAmount: number | null;   // set when financeResolution === 'revised', cannot exceed unspentAmount
}

// Mock proposal lines — Category mode (cost categories)
// In production: replaced by API response from GET /projects/{id}/forecast/roll-forward-proposal
const MOCK_PROPOSAL_LINES_CATEGORY: ProposalLine[] = [
  {
    id: 1,
    rowLabel: "Internal Labour",
    rowId: "cat-001",
    fromPeriod: "2027-01",
    unspentAmount: 2400,
    toPeriod: "2027-02",
    amount: 2400,
    pmResponse: "accepted",
    pmComment: "",
    financeResolution: "proceed", // auto-resolved for accepted rows
    financeNote: "",
    revisedToPeriod: null,
    revisedAmount: null,
  },
  {
    id: 2,
    rowLabel: "3rd Party Costs",
    rowId: "cat-002",
    fromPeriod: "2027-01",
    unspentAmount: 800,
    toPeriod: "2027-02",
    amount: 800,
    pmResponse: "challenged",
    pmComment: "We expect this to be invoiced in March, not February.",
    financeResolution: null,
    financeNote: "",
    revisedToPeriod: null,
    revisedAmount: null,
  },
];

// Mock proposal lines — Cost Centre mode (leaf cost centres only)
const MOCK_PROPOSAL_LINES_COST_CENTRE: ProposalLine[] = [
  {
    id: 1,
    rowLabel: "Projects",
    rowId: "cc-001",
    fromPeriod: "2027-01",
    unspentAmount: 2400,
    toPeriod: "2027-02",
    amount: 2400,
    pmResponse: "accepted",
    pmComment: "",
    financeResolution: "proceed", // auto-resolved for accepted rows
    financeNote: "",
    revisedToPeriod: null,
    revisedAmount: null,
  },
  {
    id: 2,
    rowLabel: "Design",
    rowId: "cc-002",
    fromPeriod: "2027-01",
    unspentAmount: 800,
    toPeriod: "2027-02",
    amount: 800,
    pmResponse: "challenged",
    pmComment: "We expect this to be invoiced in March, not February.",
    financeResolution: null,
    financeNote: "",
    revisedToPeriod: null,
    revisedAmount: null,
  },
];

// Budget rows available for selection in the Roll-Forward dropdown.
// In production: read from the same source as the Forecast grid rows.
// Category mode — flat list of cost categories
const MOCK_CATEGORY_ROWS = [
  { id: "cat-001", label: "Internal Labour" },
  { id: "cat-002", label: "3rd Party Costs" },
  { id: "cat-003", label: "External Labour" },
  { id: "cat-004", label: "Expenses" },
  { id: "cat-005", label: "Adjustments" },
];

// Cost Centre mode — leaf cost centres only (no structure/parent nodes)
const MOCK_COST_CENTRE_ROWS = [
  { id: "cc-001", label: "Projects" },
  { id: "cc-002", label: "Design" },
  { id: "cc-003", label: "Admin" },
  { id: "cc-004", label: "QHSE" },
  { id: "cc-005", label: "Offshore Techs" },
];

const RF_STAGES = [
  { key: "pending",     label: "Finance Proposes",  step: 1 },
  { key: "pm-reviewed", label: "PM Reviews",         step: 2 },
  { key: "applied",     label: "Finance Applies",    step: 3 },
] as const;

function stageIndex(status: RollForwardProposal["status"]): number {
  if (status === "pm-reviewed") return 1;
  if (status === "applied")     return 2;
  return 0; // "pending" or "cancelled"
}

function RollForwardModalShell({
  open,
  onClose,
  proposal,
  isCostCentreMode,
  currentRole,
  onStatusChange,
}: {
  open: boolean;
  onClose: () => void;
  proposal: RollForwardProposal;
  isCostCentreMode: boolean;
  currentRole: "finance" | "pm";
  onStatusChange: (status: RollForwardProposal["status"]) => void;
}) {
  const [year, month] = proposal.periodClosed.split("-").map(Number);
  const periodLabel = new Date(year, (month ?? 1) - 1, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  // Stage visibility:
  //   Stage 1 — Finance, proposal is 'pending'
  //   Stage 2 — PM role (any status)
  //   Stage 3 — Finance role + proposal is 'pm-reviewed'
  const showStage3 = currentRole === "finance" && proposal.status === "pm-reviewed";
  const showStage2 = currentRole === "pm";
  const activeStep = showStage3 ? 2 : showStage2 ? 1 : 0;

  // ── Proposal lines state ─────────────────────────────────────────────────
  // Mock — dev team replaces with API data from
  // GET /projects/{id}/forecast/roll-forward-proposal
  // Initialised per mode. The component is keyed on isCostCentreMode at the
  // call site, so it remounts (and resets) whenever the mode toggle changes.
  const initialLines = isCostCentreMode
    ? MOCK_PROPOSAL_LINES_COST_CENTRE
    : MOCK_PROPOSAL_LINES_CATEGORY;
  const [proposalLines, setProposalLines] = useState<ProposalLine[]>(initialLines);
  const nextIdRef = useRef(initialLines.length + 1);

  const updateLine = useCallback((id: number, patch: Partial<ProposalLine>) => {
    setProposalLines(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  }, []);

  const removeLine = useCallback((id: number) => {
    setProposalLines(prev => prev.filter(l => l.id !== id));
  }, []);

  const addLine = useCallback(() => {
    const id = nextIdRef.current++;
    setProposalLines(prev => [
      ...prev,
      {
        id,
        rowLabel: "", rowId: "",
        fromPeriod: proposal.periodClosed,
        unspentAmount: 0, toPeriod: "", amount: 0,
        pmResponse: null, pmComment: "",
        financeResolution: null, financeNote: "",
        revisedToPeriod: null, revisedAmount: null,
      },
    ]);
  }, [proposal.periodClosed]);

  // Rows not yet in the proposal table — used to populate the dropdown for new rows.
  // Source switches with BudgetMode: categories in Category mode, leaf cost centres
  // in Cost Centre mode (structure/parent nodes are excluded from cost centre rows).
  const allBudgetRows = isCostCentreMode ? MOCK_COST_CENTRE_ROWS : MOCK_CATEGORY_ROWS;
  const usedRowIds = new Set(proposalLines.map(l => l.rowId).filter(Boolean));
  const availableBudgetRows = allBudgetRows.filter(r => !usedRowIds.has(r.id));
  const allRowsAdded = availableBudgetRows.length === 0;

  // A line is invalid if amount exceeds unspent, or any required field is blank
  const lineErrors = proposalLines.map(l => ({
    id: l.id,
    amountExceeded: l.amount > l.unspentAmount,
    missingLabel: l.rowLabel.trim() === "",
    missingTarget: l.toPeriod === "",
  }));
  const hasErrors = lineErrors.some(e => e.amountExceeded || e.missingLabel || e.missingTarget);
  const canPropose = proposalLines.length > 0 && !hasErrors;

  const handlePropose = useCallback(() => {
    if (!canPropose) return;
    console.log("proposal submitted", proposalLines);
    toast.success("Roll-Forward Proposal sent to PM for review");
    onClose();
  }, [canPropose, proposalLines, onClose]);

  // ── PM Review handlers ────────────────────────────────────────────────────
  const setPmResponse = useCallback(
    (id: number, response: "accepted" | "challenged" | null) => {
      setProposalLines(prev =>
        prev.map(l => l.id === id ? { ...l, pmResponse: response, pmComment: response === "accepted" ? "" : l.pmComment } : l)
      );
    },
    [],
  );

  const setPmComment = useCallback((id: number, comment: string) => {
    setProposalLines(prev =>
      prev.map(l => l.id === id ? { ...l, pmComment: comment } : l)
    );
  }, []);

  const acceptAll = useCallback(() => {
    setProposalLines(prev =>
      prev.map(l => l.pmResponse === null ? { ...l, pmResponse: "accepted" as const, pmComment: "" } : l)
    );
  }, []);

  // Submit is enabled when every row has a response AND all challenged rows have a comment
  const canSubmitPm = proposalLines.length > 0 &&
    proposalLines.every(l => l.pmResponse !== null) &&
    proposalLines.every(l => l.pmResponse !== "challenged" || l.pmComment.trim() !== "");

  const handlePmSubmit = useCallback(() => {
    if (!canSubmitPm) return;
    console.log("PM response submitted", proposalLines);
    onStatusChange("pm-reviewed");
    toast.success("Response submitted. Finance has been notified.");
    onClose();
  }, [canSubmitPm, proposalLines, onStatusChange, onClose]);

  // ── Finance Resolve handlers (Stage 3) ───────────────────────────────────
  const setFinanceResolution = useCallback(
    (id: number, resolution: "proceed" | "revised" | null) => {
      setProposalLines(prev =>
        prev.map(l =>
          l.id === id
            ? {
                ...l,
                financeResolution: resolution,
                // Clear the other mode's data when switching
                financeNote: resolution === "revised" ? "" : l.financeNote,
                revisedToPeriod: resolution === "proceed" ? null : l.revisedToPeriod,
                revisedAmount: resolution === "proceed" ? null : l.revisedAmount,
              }
            : l,
        ),
      );
    },
    [],
  );

  const setFinanceNote = useCallback((id: number, note: string) => {
    setProposalLines(prev =>
      prev.map(l => l.id === id ? { ...l, financeNote: note } : l),
    );
  }, []);

  const setRevisedToPeriod = useCallback((id: number, period: string) => {
    setProposalLines(prev =>
      prev.map(l => l.id === id ? { ...l, revisedToPeriod: period } : l),
    );
  }, []);

  const setRevisedAmount = useCallback((id: number, amount: number) => {
    setProposalLines(prev =>
      prev.map(l =>
        l.id === id
          ? { ...l, revisedAmount: Math.min(amount, l.unspentAmount) }
          : l,
      ),
    );
  }, []);

  // Apply is enabled when all challenged rows have a valid resolution
  const canApply = proposalLines.length > 0 &&
    proposalLines.every(l => {
      if (l.pmResponse !== "challenged") return true; // accepted rows never block
      if (l.financeResolution === "proceed") return l.financeNote.trim() !== "";
      if (l.financeResolution === "revised")
        return l.revisedToPeriod !== null && l.revisedToPeriod !== "" &&
               l.revisedAmount !== null && l.revisedAmount > 0;
      return false; // no resolution yet
    });

  const handleApply = useCallback(() => {
    if (!canApply) return;
    console.log("roll-forward applied", proposalLines);
    onStatusChange("applied");
    toast.success("Roll-Forward applied. Forecast grid has been updated.");
    onClose();
  }, [canApply, proposalLines, onStatusChange, onClose]);

  // Format "YYYY-MM" → "Month YYYY" for display in the PM table
  const formatPeriod = (yyyyMm: string) => {
    const [y, m] = yyyyMm.split("-").map(Number);
    return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl w-full max-h-[90vh] overflow-y-auto p-0">

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
          <div>
            <DialogTitle className="text-base font-semibold">
              {showStage3
                ? "Roll-Forward Proposal — PM Response Received"
                : "Roll-Forward Proposal"}
            </DialogTitle>
            <DialogDescription className="mt-0.5 text-xs text-muted-foreground">
              Period closed:{" "}
              <span className="font-medium text-foreground">{periodLabel}</span>
              {showStage3 && proposal.reviewedBy ? (
                <>
                  {"  ·  "}Reviewed by{" "}
                  <span className="font-medium text-foreground">{proposal.reviewedBy}</span>
                  {proposal.reviewedAt && (
                    <>
                      {" on "}
                      <span className="font-medium text-foreground">
                        {new Date(proposal.reviewedAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <>
                  {"  ·  "}Proposed by{" "}
                  <span className="font-medium text-foreground">{proposal.proposedBy}</span>
                </>
              )}
            </DialogDescription>
          </div>
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Stage indicator ───────────────────────────────────────────── */}
        <div className="border-b px-6 py-4">
          <ol className="flex items-center gap-0">
            {RF_STAGES.map((stage, idx) => {
              const isActive   = idx === activeStep;
              const isComplete = idx < activeStep;
              const isLast     = idx === RF_STAGES.length - 1;

              return (
                <li key={stage.key} className="flex items-center">
                  {/* Circle */}
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className={cn(
                        "flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors",
                        isComplete
                          ? "border-primary bg-primary text-primary-foreground"
                          : isActive
                          ? "border-primary bg-primary text-primary-foreground ring-4 ring-primary/20"
                          : "border-muted-foreground/30 bg-background text-muted-foreground",
                      )}
                    >
                      {isComplete ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        stage.step
                      )}
                    </div>
                    <span
                      className={cn(
                        "text-[11px] font-medium whitespace-nowrap",
                        isActive || isComplete
                          ? "text-foreground"
                          : "text-muted-foreground",
                      )}
                    >
                      {stage.label}
                    </span>
                  </div>

                  {/* Connector arrow */}
                  {!isLast && (
                    <div className="mx-3 mb-5 flex-1">
                      <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            STAGE 1 — Finance Propose Table (shown to Finance role, pending status)
            ══════════════════════════════════════════════════════════════ */}
        {!showStage2 && !showStage3 && (
          <>
            <div className="px-6 py-5">
              {proposalLines.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  No movements proposed. Add a row to begin.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">
                        Row
                      </th>
                      <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">
                        Unspent in {periodLabel}
                      </th>
                      <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">
                        Proposed Move To
                      </th>
                      <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">
                        Amount
                      </th>
                      <th className="pb-2.5 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {proposalLines.map((line) => {
                      const err = lineErrors.find(e => e.id === line.id)!;
                      return (
                        <tr key={line.id} className="border-b last:border-0">
                          {/* Row label */}
                          <td className="py-2.5 pr-4 align-top">
                            {line.rowId ? (
                              <span className="font-medium">{line.rowLabel}</span>
                            ) : (
                              <select
                                value=""
                                onChange={e => {
                                  const selected = allBudgetRows.find(r => r.id === e.target.value);
                                  if (selected) {
                                    updateLine(line.id, {
                                      rowId: selected.id,
                                      rowLabel: selected.label,
                                      unspentAmount: 1200,
                                      amount: 1200,
                                    });
                                  }
                                }}
                                className={cn(
                                  "w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring bg-background",
                                  err.missingLabel && "border-destructive",
                                )}
                              >
                                <option value="" disabled>
                                  Select a budget row…
                                </option>
                                {availableBudgetRows.map(r => (
                                  <option key={r.id} value={r.id}>
                                    {r.label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          {/* Unspent — read-only; shows — until a row is selected */}
                          <td className="py-2.5 pr-4 align-top tabular-nums text-muted-foreground">
                            {line.rowId ? formatCurrency(line.unspentAmount) : "—"}
                          </td>
                          {/* Proposed Move To — month picker */}
                          <td className="py-2.5 pr-4 align-top">
                            <input
                              type="month"
                              value={line.toPeriod}
                              onChange={e => updateLine(line.id, { toPeriod: e.target.value })}
                              className={cn(
                                "rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring",
                                err.missingTarget && "border-destructive",
                              )}
                            />
                            {err.missingTarget && (
                              <p className="mt-0.5 text-xs text-destructive">Required</p>
                            )}
                          </td>
                          {/* Amount — editable */}
                          <td className="py-2.5 pr-4 align-top">
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={line.amount}
                              onChange={e =>
                                updateLine(line.id, { amount: parseFloat(e.target.value) || 0 })
                              }
                              className={cn(
                                "w-28 rounded border px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-ring",
                                err.amountExceeded && "border-destructive",
                              )}
                            />
                            {err.amountExceeded && (
                              <p className="mt-0.5 text-xs text-destructive">
                                Cannot exceed {formatCurrency(line.unspentAmount)}
                              </p>
                            )}
                          </td>
                          {/* Delete */}
                          <td className="py-2.5 align-top">
                            <button
                              type="button"
                              onClick={() => removeLine(line.id)}
                              className="text-muted-foreground transition-colors hover:text-destructive"
                              aria-label={`Remove ${line.rowLabel || "row"}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Add row — hidden when all budget rows are already in the proposal */}
              {allRowsAdded ? (
                <p className="mt-3 text-xs text-muted-foreground italic">
                  All budget rows have been added.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={addLine}
                  className="mt-3 flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Row
                </button>
              )}
            </div>

            {/* Stage 1 footer */}
            <div className="flex items-center justify-between border-t px-6 py-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handlePropose}
                disabled={!canPropose}
                className="gap-1"
              >
                Propose
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════
            STAGE 2 — PM Review Table (shown to PM role)
            All columns read-only. PM can Accept or Challenge each line.
            ══════════════════════════════════════════════════════════════ */}
        {showStage2 && (
          <>
            <div className="px-6 py-5">

              {/* "Accept All" toolbar — right-aligned above the table */}
              <div className="mb-3 flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={acceptAll}
                  disabled={proposalLines.every(l => l.pmResponse !== null)}
                >
                  Accept All
                </Button>
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">Row</th>
                    <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">
                      Unspent in {periodLabel}
                    </th>
                    <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">
                      Proposed Move To
                    </th>
                    <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">
                      Amount
                    </th>
                    <th className="pb-2.5 text-xs font-medium text-muted-foreground">
                      PM Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {proposalLines.map((line) => {
                    const isAccepted   = line.pmResponse === "accepted";
                    const isChallenged = line.pmResponse === "challenged";
                    const isResponded  = isAccepted || isChallenged;

                    return (
                      <>
                        {/* Main data row */}
                        <tr
                          key={line.id}
                          className={cn(
                            "border-b transition-colors",
                            isAccepted   && "border-l-4 border-l-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20",
                            isChallenged && "border-l-4 border-l-amber-400 bg-amber-50/40 dark:bg-amber-950/20",
                          )}
                        >
                          {/* Row label — read-only */}
                          <td className={cn("py-2.5 pl-3 pr-4 align-middle font-medium", isAccepted && "text-muted-foreground")}>
                            {line.rowLabel}
                          </td>

                          {/* Unspent — read-only */}
                          <td className={cn("py-2.5 pr-4 align-middle tabular-nums", isAccepted ? "text-muted-foreground" : "text-foreground")}>
                            {formatCurrency(line.unspentAmount)}
                          </td>

                          {/* Proposed Move To — read-only */}
                          <td className={cn("py-2.5 pr-4 align-middle", isAccepted && "text-muted-foreground")}>
                            {formatPeriod(line.toPeriod)}
                          </td>

                          {/* Amount — read-only */}
                          <td className={cn("py-2.5 pr-4 align-middle tabular-nums font-medium", isAccepted && "text-muted-foreground")}>
                            {formatCurrency(line.amount)}
                          </td>

                          {/* PM Action buttons */}
                          <td className="py-2.5 align-middle">
                            <div className="flex items-center gap-2">
                              {/* ── Accepted state ── */}
                              {isAccepted && (
                                <>
                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                    <Check className="h-3 w-3" />
                                    Accepted
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setPmResponse(line.id, null)}
                                    className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                                  >
                                    Change
                                  </button>
                                </>
                              )}

                              {/* ── Challenged state ── */}
                              {isChallenged && (
                                <>
                                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                    <AlertTriangle className="h-3 w-3" />
                                    Challenged
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setPmResponse(line.id, null)}
                                    className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                                  >
                                    Change
                                  </button>
                                </>
                              )}

                              {/* ── No response yet ── */}
                              {!isResponded && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => setPmResponse(line.id, "accepted")}
                                    className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50 dark:border-emerald-700 dark:bg-transparent dark:text-emerald-400 dark:hover:bg-emerald-950/40"
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPmResponse(line.id, "challenged")}
                                    className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50 dark:border-amber-700 dark:bg-transparent dark:text-amber-400 dark:hover:bg-amber-950/40"
                                  >
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    Challenge
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Inline comment row — only shown when challenged */}
                        {isChallenged && (
                          <tr key={`${line.id}-comment`} className="border-b bg-amber-50/60 dark:bg-amber-950/10">
                            <td colSpan={5} className="px-3 pb-3 pt-1">
                              <label className="mb-1 block text-[11px] font-medium text-amber-700 dark:text-amber-400">
                                Describe your challenge{" "}
                                <span className="text-destructive">*</span>
                              </label>
                              <textarea
                                value={line.pmComment}
                                onChange={e => setPmComment(line.id, e.target.value.slice(0, 500))}
                                placeholder="Describe your challenge..."
                                rows={2}
                                className="w-full resize-none rounded border border-amber-300 bg-white px-2.5 py-1.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-amber-400 dark:border-amber-700 dark:bg-background"
                              />
                              <p className="mt-0.5 text-right text-[11px] text-muted-foreground">
                                {line.pmComment.length} / 500
                              </p>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Stage 2 footer */}
            <div className="flex items-center justify-between border-t px-6 py-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={acceptAll}
                  disabled={proposalLines.every(l => l.pmResponse !== null)}
                >
                  Accept All
                </Button>
                <Button
                  onClick={handlePmSubmit}
                  disabled={!canSubmitPm}
                  className="gap-1"
                >
                  Submit Response
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════
            STAGE 3 — Finance Resolve (shown when Finance + pm-reviewed)
            Accepted rows locked; challenged rows require Finance action.
            ══════════════════════════════════════════════════════════════ */}
        {showStage3 && (
          <>
            <div className="px-6 py-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground w-36">Row</th>
                    <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">Proposed</th>
                    <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">PM Response</th>
                    <th className="pb-2.5 pr-4 text-xs font-medium text-muted-foreground">PM Comment</th>
                    <th className="pb-2.5 text-xs font-medium text-muted-foreground">Finance Action</th>
                  </tr>
                </thead>
                <tbody>
                  {proposalLines.map((line) => {
                    const isAccepted   = line.pmResponse === "accepted";
                    const isChallenged = line.pmResponse === "challenged";
                    const hasResolution = line.financeResolution !== null;
                    // Blue border once Finance has acted on a challenged row
                    const isActioned = isChallenged && hasResolution;

                    return (
                      <>
                        {/* Main data row */}
                        <tr
                          key={line.id}
                          className={cn(
                            "border-b transition-colors",
                            isAccepted  && "border-l-4 border-l-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20",
                            isChallenged && !isActioned && "border-l-4 border-l-amber-400 bg-amber-50/40 dark:bg-amber-950/20",
                            isActioned  && "border-l-4 border-l-blue-400 bg-blue-50/40 dark:bg-blue-950/20",
                          )}
                        >
                          {/* Row label */}
                          <td className={cn(
                            "py-2.5 pl-3 pr-4 align-middle font-medium",
                            isAccepted && "text-muted-foreground",
                          )}>
                            {line.rowLabel}
                          </td>

                          {/* Proposed (amount → period) */}
                          <td className={cn("py-2.5 pr-4 align-middle tabular-nums", isAccepted && "text-muted-foreground")}>
                            {line.financeResolution === "revised" ? (
                              <span className="flex flex-col gap-0.5">
                                <span className="line-through text-muted-foreground/60 text-xs">
                                  {formatCurrency(line.amount)} → {formatPeriod(line.toPeriod)}
                                </span>
                                <span className="flex items-center gap-1 text-blue-700 dark:text-blue-300">
                                  {formatCurrency(line.revisedAmount ?? line.amount)} → {formatPeriod(line.revisedToPeriod ?? line.toPeriod)}
                                  <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                                    Revised
                                  </span>
                                </span>
                              </span>
                            ) : (
                              <span>{formatCurrency(line.amount)} → {formatPeriod(line.toPeriod)}</span>
                            )}
                          </td>

                          {/* PM Response badge */}
                          <td className="py-2.5 pr-4 align-middle">
                            {isAccepted && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                <Check className="h-3 w-3" /> Accepted
                              </span>
                            )}
                            {isChallenged && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                <AlertTriangle className="h-3 w-3" /> Challenged
                              </span>
                            )}
                          </td>

                          {/* PM Comment */}
                          <td className="py-2.5 pr-4 align-middle max-w-[200px]">
                            {isChallenged && line.pmComment ? (
                              <span className="italic text-muted-foreground text-xs leading-snug">
                                "{line.pmComment}"
                              </span>
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </td>

                          {/* Finance Action */}
                          <td className="py-2.5 align-middle">
                            {isAccepted && (
                              <span className="text-muted-foreground/50 text-xs">—</span>
                            )}
                            {isChallenged && !hasResolution && (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setFinanceResolution(line.id, "proceed")}
                                  className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-50 dark:border-blue-700 dark:bg-transparent dark:text-blue-400 dark:hover:bg-blue-950/40"
                                >
                                  Proceed
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setFinanceResolution(line.id, "revised")}
                                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-transparent dark:text-slate-300 dark:hover:bg-slate-800/40"
                                >
                                  Revise
                                </button>
                              </div>
                            )}
                            {isChallenged && hasResolution && (
                              <div className="flex items-center gap-2">
                                <span className={cn(
                                  "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold",
                                  line.financeResolution === "proceed"
                                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
                                )}>
                                  {line.financeResolution === "proceed" ? "Proceeding" : "Revising"}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setFinanceResolution(line.id, null)}
                                  className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                                >
                                  Change
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>

                        {/* Proceed — inline Finance note field */}
                        {isChallenged && line.financeResolution === "proceed" && (
                          <tr key={`${line.id}-note`} className="border-b bg-blue-50/60 dark:bg-blue-950/10">
                            <td colSpan={5} className="px-3 pb-3 pt-1">
                              <label className="mb-1 block text-[11px] font-medium text-blue-700 dark:text-blue-400">
                                Finance note — why are you proceeding?{" "}
                                <span className="text-destructive">*</span>
                              </label>
                              <textarea
                                value={line.financeNote}
                                onChange={e => setFinanceNote(line.id, e.target.value.slice(0, 500))}
                                placeholder="Add a note explaining why you are proceeding..."
                                rows={2}
                                className="w-full resize-none rounded border border-blue-300 bg-white px-2.5 py-1.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-blue-400 dark:border-blue-700 dark:bg-background"
                              />
                              <p className="mt-0.5 text-right text-[11px] text-muted-foreground">
                                {line.financeNote.length} / 500
                              </p>
                            </td>
                          </tr>
                        )}

                        {/* Revise — inline editable period + amount fields */}
                        {isChallenged && line.financeResolution === "revised" && (
                          <tr key={`${line.id}-revise`} className="border-b bg-slate-50/60 dark:bg-slate-900/20">
                            <td colSpan={5} className="px-3 pb-3 pt-1">
                              <p className="mb-2 text-[11px] font-medium text-slate-600 dark:text-slate-400">
                                Enter revised values for this row:
                              </p>
                              <div className="flex items-end gap-4 flex-wrap">
                                <div>
                                  <label className="mb-1 block text-[11px] text-muted-foreground">
                                    Move To (revised)
                                  </label>
                                  <input
                                    type="month"
                                    value={line.revisedToPeriod ?? ""}
                                    onChange={e => setRevisedToPeriod(line.id, e.target.value)}
                                    className="rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-[11px] text-muted-foreground">
                                    Amount (max {formatCurrency(line.unspentAmount)})
                                  </label>
                                  <input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    max={line.unspentAmount}
                                    value={line.revisedAmount ?? ""}
                                    onChange={e => setRevisedAmount(line.id, parseFloat(e.target.value) || 0)}
                                    className="w-28 rounded border px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Stage 3 footer */}
            <div className="flex items-center justify-between border-t px-6 py-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleApply}
                disabled={!canApply}
                className="gap-1"
              >
                Apply Roll-Forward
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
}

export function ForecastTab() {
  // ── EAC settings from company-wide context ─────────────────────────────────
  const { eacMethod, showHoursToComplete } = useEacSettings();

  // ── PM's Estimate to Complete (£) — only used when eacMethod === 're-estimate'
  // Stored in local component state; no API call. Recalculates EAC card on change.
  const [pmEtc, setPmEtc] = useState<string>('');

  // ── Base dates ─────────────────────────────────────────────────────────────
  const today            = useMemo(() => new Date(), []);
  const projectStartDate = useMemo(() => addMonths(today, -18), [today]);
  const projectEndDate   = useMemo(() => addMonths(today,  12), [today]);

  // ── Last actuals month — system-driven (mock: July 2026) ──────────────────
  // In production this is a prop from the parent populated via
  // GET /projects/{id}/forecast/last-actuals-month
  const [liveLastActualsMonth, setLiveLastActualsMonth] = useState<MonthKey>("2026-07");

  // ── Project type — read from project record ───────────────────────────────
  // Drives Revenue sub-tab structure: fixed-price → milestone grid,
  // time-and-materials → BudgetMode-driven grid, retainer → Coming Soon.
  const { project } = useProjectDetailsContext();
  const projectType: ProjectType = project?.type ?? "time-and-materials";

  // ── DEV-ONLY: Budget mode toggle ──────────────────────────────────────────
  // In production, read from OrgBudgetConfiguration.BudgetMode via
  // useBudgetPermissions() → isCostCentreMode / isCategoryMode.
  // Remove this state and the toolbar toggle when wiring up the real hook.
  const [mockBudgetMode, setMockBudgetMode] = useState<BudgetMode>("category");

  // ── DEV-ONLY: Role toggle ─────────────────────────────────────────────────
  // Switch to 'pm' to preview the Stage 2 PM Review view inside the modal.
  // In production, read the current user's role from the auth/permissions context.
  // Remove this state and the toolbar toggle when wiring up real auth.
  const [mockCurrentRole, setMockCurrentRole] = useState<"finance" | "pm">("finance");

  // ── Roll-Forward proposal — reactive state ────────────────────────────────
  // Starts as 'pending'. PM submit → 'pm-reviewed'. Banner and modal both
  // react to this single source of truth.
  const [proposalStatus, setProposalStatus] = useState<RollForwardProposal["status"]>(
    mockPendingProposal.status,
  );
  const activeProposal = useMemo<RollForwardProposal>(
    () => ({ ...mockPendingProposal, status: proposalStatus }),
    [proposalStatus],
  );

  // ── Forecast end date (PM-editable) ───────────────────────────────────────
  const [forecastEndDateStr, setForecastEndDateStr] = useState<string>(() => {
    const d = addMonths(new Date(), 12);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });

  const effectiveEndDate = useMemo(() => {
    const parsed = forecastEndDateStr ? new Date(forecastEndDateStr) : projectEndDate;
    const minEnd = addMonths(today, 3);
    return new Date(Math.max(projectEndDate.getTime(), parsed.getTime(), minEnd.getTime()));
  }, [forecastEndDateStr, projectEndDate, today]);

  const months = useMemo(
    () => generateMonths(projectStartDate, effectiveEndDate),
    [projectStartDate, effectiveEndDate],
  );

  const actualMonths = useMemo(() => months.filter((m) => m <= liveLastActualsMonth), [months, liveLastActualsMonth]);
  const futureMonths = useMemo(() => months.filter((m) => m >  liveLastActualsMonth), [months, liveLastActualsMonth]);

  // ── Cost grid state — Category mode ───────────────────────────────────────
  const [costGroups, setCostGroups] = useState<CostGroup[]>(() => {
    const t = new Date();
    return buildInitialCostGroups(
      generateMonths(addMonths(t, -18), addMonths(t, 12)),
      toMonthKey(addMonths(t, -2)),
    );
  });

  // ── Cost grid state — Cost Centre mode ────────────────────────────────────
  const [costCentreGroups, setCostCentreGroups] = useState<CostGroup[]>(() => {
    const t = new Date();
    return buildInitialCostCentreGroups(
      generateMonths(addMonths(t, -18), addMonths(t, 12)),
      toMonthKey(addMonths(t, -2)),
    );
  });

  // ── T&M revenue grid state — grouped (mirrors CostGroup[] used by CostForecastGrid)
  const [tmRevenueGroups, setTmRevenueGroups] = useState<CostGroup[]>(() => {
    const t = new Date();
    return buildInitialTmRevenueGroups(
      generateMonths(addMonths(t, -18), addMonths(t, 12)),
      toMonthKey(addMonths(t, -2)),
    );
  });

  // ── Fixed-price milestone state ────────────────────────────────────────────
  const [milestoneRows, setMilestoneRows] = useState<MilestoneRow[]>(INITIAL_MILESTONE_ROWS);

  // ── Cell update handlers ──────────────────────────────────────────────────
  const handleCostCellUpdate = useCallback((rowId: string, month: MonthKey, value: number) => {
    setCostGroups((prev) =>
      prev.map((g) => ({
        ...g,
        rows: g.rows.map((r) => r.id === rowId ? { ...r, values: { ...r.values, [month]: value } } : r),
      })),
    );
  }, []);

  const handleCostCentreCellUpdate = useCallback((rowId: string, month: MonthKey, value: number) => {
    setCostCentreGroups((prev) =>
      prev.map((g) => ({
        ...g,
        rows: g.rows.map((r) => r.id === rowId ? { ...r, values: { ...r.values, [month]: value } } : r),
      })),
    );
  }, []);

  const handleTmRevenueCellUpdate = useCallback((rowId: string, month: MonthKey, value: number) => {
    setTmRevenueGroups((prev) =>
      prev.map((g) => ({
        ...g,
        rows: g.rows.map((r) => r.id === rowId ? { ...r, values: { ...r.values, [month]: value } } : r),
      })),
    );
  }, []);

  const handleMilestoneUpdate = useCallback((id: string, forecastDate: MonthKey | null) => {
    setMilestoneRows((prev) => prev.map((m) => m.id === id ? { ...m, forecastDate } : m));
  }, []);

  // ── Roll-Forward / Override flow ──────────────────────────────────────────
  const closingMonth  = liveLastActualsMonth;
  const defaultMoveTo = useMemo(
    () => toMonthKey(addMonths(monthKeyToDate(liveLastActualsMonth), 1)),
    [liveLastActualsMonth],
  );

  const [showRollForward,      setShowRollForward]      = useState(false);
  const [rollForwardModalOpen, setRollForwardModalOpen] = useState(false);
  const [showOverrideModal,    setShowOverrideModal]    = useState(false);
  const [overrideDraftMonth, setOverrideDraftMonth] = useState<MonthKey>(liveLastActualsMonth);

  const [rfCostActions, setRfCostActions] = useState<Record<string, RollForwardRowState>>(() =>
    Object.fromEntries(MOCK_RF_COST_ROWS.map((r) => [r.id, { action: "Confirm" as RollForwardAction, moveTo: "" }])),
  );
  const [rfRevenueActions, setRfRevenueActions] = useState<Record<string, RollForwardRowState>>(() =>
    Object.fromEntries(MOCK_RF_REVENUE_ROWS.map((r) => [r.id, { action: "Confirm" as RollForwardAction, moveTo: "" }])),
  );

  const openRollForward = useCallback(() => {
    setRfCostActions(Object.fromEntries(MOCK_RF_COST_ROWS.map((r) => [r.id, { action: "Confirm" as RollForwardAction, moveTo: defaultMoveTo }])));
    setRfRevenueActions(Object.fromEntries(MOCK_RF_REVENUE_ROWS.map((r) => [r.id, { action: "Confirm" as RollForwardAction, moveTo: defaultMoveTo }])));
    setShowRollForward(true);
  }, [defaultMoveTo]);

  const handleCostActionChange = useCallback((id: string, field: keyof RollForwardRowState, value: string) => {
    setRfCostActions((prev) => ({ ...prev, [id]: { ...prev[id]!, [field]: value } }));
  }, []);

  const handleRevenueActionChange = useCallback((id: string, field: keyof RollForwardRowState, value: string) => {
    setRfRevenueActions((prev) => ({ ...prev, [id]: { ...prev[id]!, [field]: value } }));
  }, []);

  const handleApply = useCallback(() => {
    for (const row of MOCK_RF_COST_ROWS) {
      const state = rfCostActions[row.id];
      if (!state || state.action === "Zero") continue;
      const targetMonth = state.moveTo || defaultMoveTo;
      setCostGroups((prev) =>
        prev.map((g) => ({
          ...g,
          rows: g.rows.map((r) =>
            r.id === row.gridRowId
              ? { ...r, values: { ...r.values, [targetMonth]: (r.values[targetMonth] ?? 0) + row.unspent } }
              : r,
          ),
        })),
      );
    }
    // NOTE: We do NOT advance liveLastActualsMonth here — the month was already
    // locked upstream when the financial period closed. Apply only moves amounts.
    setShowRollForward(false);
    toast.success(`${formatMonthLabel(closingMonth)} closed. ${formatMonthLabel(defaultMoveTo)} forecast updated.`);
  }, [rfCostActions, closingMonth, defaultMoveTo]);

  const handleOverrideApply = useCallback(() => {
    setLiveLastActualsMonth(overrideDraftMonth);
    setShowOverrideModal(false);
    toast.success("Last Actuals Month overridden for this project.");
    setTimeout(() => openRollForward(), 0);
  }, [overrideDraftMonth, openRollForward]);

  // ── Derived summary metrics ───────────────────────────────────────────────
  const totalBudget        = useMemo(() => computeCostBudget(costGroups), [costGroups]);
  const actualsToDate      = useMemo(() => computeCostActuals(costGroups, actualMonths), [costGroups, actualMonths]);
  const forecastToComplete = useMemo(() => computeCostFTC(costGroups, futureMonths), [costGroups, futureMonths]);

  // EAC — computed via calculateEAC() using the company-wide eacMethod from context.
  // For the forecast tab a synthetic completionPercent is derived from cost spend.
  // When eacMethod is 're-estimate', pmEtc drives the figure; if empty/0, falls back
  // to Simple automatically inside calculateEAC().
  const eacResult = useMemo(() => {
    const completionPct = totalBudget > 0 ? Math.min((actualsToDate / totalBudget) * 100, 100) : 0;
    const etcNumeric = eacMethod === 're-estimate' ? Number(pmEtc) || 0 : 0;
    return calculateEAC(
      {
        actualCosts: actualsToDate,
        totalBudget,
        completionPercent: completionPct,
        estimateToComplete: etcNumeric,
      },
      eacMethod,
    );
  }, [actualsToDate, totalBudget, eacMethod, pmEtc]);
  const eac = eacResult.eac;

  // ── HTC-derived metrics ───────────────────────────────────────────────────
  // htcEnabled: both the org toggle (from EacSettings context) AND the mock
  // showHoursToComplete flag must be true.
  const htcEnabled = showHoursToComplete && MOCK_HTC_DATA.showHoursToComplete;

  // Forecast to Complete — HTC-based when on, linear projection when off.
  const htcForecastToComplete = htcEnabled
    ? MOCK_HTC_DATA.projectTotalHoursToComplete * MOCK_HTC_DATA.costRate
    : null;
  const activeForecastToComplete = htcForecastToComplete ?? forecastToComplete;

  // Overrun risk: HTC hours exceed remaining capacity
  const htcOverrunRisk = htcEnabled
    ? MOCK_HTC_DATA.projectTotalHoursToComplete > MOCK_HTC_DATA.remainingCapacityHours
    : false;

  // EAC method badge label (reflects actual method used, e.g. fallback to Simple)
  const eacMethodBadgeLabel: Record<string, string> = {
    'simple':           'Simple',
    'performance-based':'CPI',
    're-estimate':      'Re-estimate',
  };
  const activeEacMethodLabel = eacMethodBadgeLabel[eacResult.method] ?? 'Simple';

  // HTC rows to pass to CostForecastGrid — only when HTC is enabled
  const activeHtcRows = htcEnabled ? MOCK_HTC_DATA.rows : undefined;

  // forecastRevenue — branches on project.type (live from project record)
  const forecastRevenue = useMemo(() => {
    if (projectType === "fixed-price")        return computeFixedPriceRevenueForecast(milestoneRows);
    if (projectType === "time-and-materials") return computeCostFTC(tmRevenueGroups, months) + computeCostActuals(tmRevenueGroups, actualMonths);
    return 0; // retainer — not supported in Phase 1
  }, [projectType, milestoneRows, tmRevenueGroups, months, actualMonths]);

  const forecastMargin   = forecastRevenue - eac;
  const isMarginPositive = forecastMargin >= 0;

  // actualInvoiced — branches on project.type
  const actualInvoiced = projectType === "fixed-price" ? MOCK_ACTUAL_INVOICED_FP : MOCK_ACTUAL_INVOICED_TM;

  return (
    <TooltipProvider>
      <div className="space-y-4">

        {/* ── 8 summary metric cards ─────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-3">

          {/* 1. Cost Budget */}
          <MetricCard
            label="Cost Budget"
            value={
              <div className="flex flex-col gap-0.5">
                <span>{formatCurrency(totalBudget)}</span>
                <span className="text-[11px] font-normal text-muted-foreground leading-tight">Approved cost budget</span>
              </div>
            }
            icon={<DollarSign className="h-3.5 w-3.5" />}
            tooltip="Approved cost budget for this project."
          />

          {/* 2. Actual Costs */}
          <MetricCard
            label="Actual Costs"
            value={
              <div className="flex flex-col gap-0.5">
                <span>{formatCurrency(actualsToDate)}</span>
                <span className="text-[11px] font-normal text-muted-foreground leading-tight">Posted to date</span>
              </div>
            }
            icon={<Receipt className="h-3.5 w-3.5" />}
            tooltip="Sum of all posted actual costs to date."
          />

          {/* 3. Forecast to Complete */}
          <MetricCard
            label="Forecast to Complete"
            value={
              <div className="flex flex-col gap-0.5">
                <span>{formatCurrency(activeForecastToComplete)}</span>
                <span className="text-[11px] font-normal text-muted-foreground leading-tight">Remaining forecast (editable)</span>
              </div>
            }
            icon={<TrendingUp className="h-3.5 w-3.5" />}
            tooltip={htcEnabled ? `Based on ${MOCK_HTC_DATA.projectTotalHoursToComplete} hrs × £${MOCK_HTC_DATA.costRate}/hr (HTC).` : "Sum of all editable future forecast cost cells."}
          />

          {/* 4. EAC — shows active method badge */}
          <MetricCard
            label="EAC"
            value={
              <div className="flex flex-col gap-0.5">
                <span>{formatCurrency(eac)}</span>
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-[11px] font-normal text-muted-foreground leading-tight">Actuals + forecast</span>
                  <span className="text-[11px] text-muted-foreground/60">·</span>
                  <Badge
                    variant="outline"
                    className="w-fit text-[10px] px-1.5 py-0 font-normal border-muted-foreground/30 bg-muted/50 text-muted-foreground"
                  >
                    {activeEacMethodLabel}
                  </Badge>
                </div>
              </div>
            }
            icon={<Calculator className="h-3.5 w-3.5" />}
            tooltip={`Estimate at Completion — ${activeEacMethodLabel} method.`}
          />

          {/* 5. Cost Variance — £ + % + Over/Under badge */}
          {(() => {
            const cvVariance = eac - totalBudget;
            const cvIsOver   = cvVariance > 0;
            const cvIsNeutral = cvVariance === 0;
            const cvPct = totalBudget !== 0 ? (cvVariance / totalBudget) * 100 : 0;
            const cvAccent: MetricCardProps["accent"] = cvIsNeutral ? "default" : cvIsOver ? "negative" : "positive";
            const cvSign = cvVariance > 0 ? "+" : "";
            return (
              <MetricCard
                label="Cost Variance"
                value={
                  <div className="flex flex-col gap-0.5">
                    <span>{cvSign}{formatCurrency(cvVariance)} / {cvSign}{cvPct.toFixed(1)}%</span>
                    <span className="text-[11px] font-normal text-muted-foreground leading-tight">EAC vs cost budget</span>
                    {!cvIsNeutral && (
                      <Badge
                        variant="outline"
                        className={cn(
                          "w-fit text-[10px] px-1.5 py-0 font-normal mt-0.5",
                          cvIsOver
                            ? "border-red-300 text-red-600 dark:border-red-800 dark:text-red-400"
                            : "border-emerald-300 text-emerald-600 dark:border-emerald-800 dark:text-emerald-400",
                        )}
                      >
                        {cvIsOver ? "Over Budget" : "Under Budget"}
                      </Badge>
                    )}
                  </div>
                }
                icon={cvIsOver ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                accent={cvAccent}
                tooltip="EAC minus Cost Budget. Positive (red) = over budget; negative (green) = under budget."
              />
            );
          })()}

          {/* 6. Sales Budget */}
          <MetricCard
            label="Sales Budget"
            value={
              <div className="flex flex-col gap-0.5">
                <span>{formatCurrency(forecastRevenue)}</span>
                <span className="text-[11px] font-normal text-muted-foreground leading-tight">Total sales budget</span>
              </div>
            }
            icon={<TrendingUp className="h-3.5 w-3.5" />}
            tooltip="Total sales budget for this project."
          />

          {/* 7. Forecast Margin — £ + % */}
          {(() => {
            const fmPct = forecastRevenue !== 0 ? (forecastMargin / forecastRevenue) * 100 : 0;
            return (
              <MetricCard
                label="Forecast Margin"
                value={
                  <div className="flex flex-col gap-0.5">
                    <span>{formatCurrency(forecastMargin)} / {fmPct.toFixed(1)}%</span>
                    <span className="text-[11px] font-normal text-muted-foreground leading-tight">Sales budget − EAC</span>
                  </div>
                }
                icon={isMarginPositive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                accent={isMarginPositive ? "positive" : "negative"}
                tooltip="Sales Budget minus EAC."
              />
            );
          })()}

          {/* 8. Forecast End Date */}
          <ForecastEndDateCard value={forecastEndDateStr} onChange={setForecastEndDateStr} overrunRisk={htcOverrunRisk} />
        </div>

        {/* ── Re-estimate ETC input — visible only when eacMethod is 're-estimate' ── */}
        {eacMethod === 're-estimate' && (
          <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20 px-4 py-3">
            <Calculator className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <label htmlFor="pm-etc-input" className="text-sm font-medium text-amber-800 dark:text-amber-300 shrink-0 whitespace-nowrap">
              Estimate to Complete (£)
            </label>
            <input
              id="pm-etc-input"
              type="number"
              min={0}
              step={0.01}
              value={pmEtc}
              onChange={(e) => setPmEtc(e.target.value)}
              placeholder="Enter PM's ETC estimate"
              className="flex-1 min-w-0 max-w-xs rounded-md border border-amber-300 dark:border-amber-700 bg-white dark:bg-amber-950 px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
            <p className="text-xs text-amber-700 dark:text-amber-400 hidden sm:block">
              EAC card updates in real time. Leave blank to fall back to Simple method.
            </p>
          </div>
        )}

        {/* ── Roll-Forward pending banner ────────────────────────────────── */}
        <RollForwardBanner
          proposal={activeProposal}
          onReview={() => setRollForwardModalOpen(true)}
        />

        {/* ── Costs / Revenue sub-tabs + toolbar ────────────────────────── */}
        <Tabs defaultValue="costs">
          <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <TabsList>
                <TabsTrigger value="costs" className="gap-1.5">
                  <BarChart3 className="h-3.5 w-3.5" />
                  Costs
                </TabsTrigger>
                <TabsTrigger value="revenue" className="gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5" />
                  Sales
                </TabsTrigger>
              </TabsList>

            </div>

            {/* DEV-ONLY: Budget mode toggle — remove when useBudgetPermissions() is wired up */}
            <div className="flex items-center gap-1.5 rounded border border-dashed border-muted-foreground/30 px-2 py-1">
              <span className="text-[10px] font-mono text-muted-foreground/60 shrink-0">DEV — Mock budget mode:</span>
              <button
                type="button"
                onClick={() => setMockBudgetMode("category")}
                className={cn(
                  "rounded px-2 py-0.5 text-[10px] font-medium transition-colors",
                  mockBudgetMode === "category"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                Category
              </button>
              <button
                type="button"
                onClick={() => setMockBudgetMode("cost-centre")}
                className={cn(
                  "rounded px-2 py-0.5 text-[10px] font-medium transition-colors",
                  mockBudgetMode === "cost-centre"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                Cost Centre
              </button>
            </div>

            {/* DEV-ONLY: Role toggle — remove when real auth/permissions is wired up */}
            <div className="flex items-center gap-1.5 rounded border border-dashed border-muted-foreground/30 px-2 py-1">
              <span className="text-[10px] font-mono text-muted-foreground/60 shrink-0">DEV — Mock role:</span>
              <button
                type="button"
                onClick={() => setMockCurrentRole("finance")}
                className={cn(
                  "rounded px-2 py-0.5 text-[10px] font-medium transition-colors",
                  mockCurrentRole === "finance"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                Finance
              </button>
              <button
                type="button"
                onClick={() => setMockCurrentRole("pm")}
                className={cn(
                  "rounded px-2 py-0.5 text-[10px] font-medium transition-colors",
                  mockCurrentRole === "pm"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                PM
              </button>
            </div>

            {/* Toolbar right: legend + override link */}
            <div className="flex flex-col items-end gap-0.5 shrink-0">
              <p className="text-xs text-muted-foreground">
                <Lock className="h-3 w-3 inline mr-1 mb-px" />
                <strong>{formatMonthLabel(liveLastActualsMonth)}</strong> and earlier = locked actuals
                {" · "}
                <strong>{formatMonthLabel(defaultMoveTo)}</strong> onwards = editable forecast
              </p>

            </div>
          </div>

          {/* ── Costs sub-tab — branches on BudgetMode ───────────────────── */}
          <TabsContent value="costs" className="mt-0">
            {mockBudgetMode === "category" ? (
              <CostForecastGrid
                groups={costGroups}
                months={months}
                lastActualsMonth={liveLastActualsMonth}
                onUpdateCell={handleCostCellUpdate}
                categoryLabel="Cost Category"
                htcRows={activeHtcRows}
              />
            ) : (
              <CostForecastGrid
                groups={costCentreGroups}
                months={months}
                lastActualsMonth={liveLastActualsMonth}
                onUpdateCell={handleCostCentreCellUpdate}
                categoryLabel="Cost Centre"
                htcRows={activeHtcRows}
              />
            )}
          </TabsContent>

          {/* ── Revenue sub-tab ──────────────────────────────────────────── */}
          {/* Branched on project.type from the project record:             */}
          {/*   fixed-price       → milestone grid (BudgetMode irrelevant)  */}
          {/*   time-and-materials → BudgetMode-driven grid (same as Costs) */}
          {/*   retainer          → Coming Soon placeholder                 */}
          <TabsContent value="revenue" className="mt-0">
            {projectType === "fixed-price" && (
              <FixedPriceMilestoneGrid
                milestones={milestoneRows}
                months={months}
                lastActualsMonth={liveLastActualsMonth}
                actualInvoiced={MOCK_ACTUAL_INVOICED_FP}
                onUpdateMilestone={handleMilestoneUpdate}
              />
            )}

            {projectType === "time-and-materials" && (() => {
              // T&M Revenue — same grouped structure as Costs sub-tab, but uses
              // SalesCategory rows (from SalesBudget). BudgetMode toggle applies.
              const tmActualMonths  = months.filter((m) => m <= liveLastActualsMonth);
              const tmFutureMonths  = months.filter((m) => m >  liveLastActualsMonth);
              const totalForecastRev = computeCostFTC(tmRevenueGroups, months) + computeCostActuals(tmRevenueGroups, tmActualMonths);
              const wip              = computeCostActuals(tmRevenueGroups, tmActualMonths) - MOCK_ACTUAL_INVOICED_TM;

              return (
                <div className="space-y-2">
                  {(() => {
                    // Build per-month Actual Invoiced values (distributed evenly across actual months)
                    const perMonthInvoiced = tmActualMonths.length > 0
                      ? Math.round(MOCK_ACTUAL_INVOICED_TM / tmActualMonths.length)
                      : 0;
                    const invoicedValues: Partial<Record<MonthKey, number>> = Object.fromEntries(
                      tmActualMonths.map((m) => [m, perMonthInvoiced])
                    );

                    // Per-month WIP (forecast − invoiced for each actual month)
                    const tmActualColSum = (m: MonthKey) =>
                      tmRevenueGroups.reduce((gs, g) => gs + g.rows.reduce((rs, r) => rs + (r.values[m] ?? 0), 0), 0);
                    const wipValues: Partial<Record<MonthKey, number>> = Object.fromEntries(
                      tmActualMonths.map((m) => [m, Math.max(0, tmActualColSum(m) - perMonthInvoiced)])
                    );
                    const wipTotal = Math.max(0, wip);
                    const wipClass = wip >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500";

                    const tmSummaryRows: SummaryGridRow[] = [
                      {
                        label: "Actual Invoiced (from FINS)",
                        values: invoicedValues,
                        total: MOCK_ACTUAL_INVOICED_TM,
                        futureDash: true,
                        valueClass: "text-muted-foreground",
                      },
                      {
                        label: "WIP",
                        values: wipValues,
                        total: wipTotal,
                        futureDash: true,
                        valueClass: wipClass,
                      },
                    ];

                    const revenueGroups = mockBudgetMode === "category" ? tmRevenueGroups : costCentreGroups;
                    const revCategoryLabel = mockBudgetMode === "category" ? "Revenue Category" : "Cost Centre (Revenue)";
                    const revHandler = mockBudgetMode === "category" ? handleTmRevenueCellUpdate : handleCostCentreCellUpdate;

                    return (
                      <CostForecastGrid
                        groups={revenueGroups}
                        months={months}
                        lastActualsMonth={liveLastActualsMonth}
                        onUpdateCell={revHandler}
                        categoryLabel={revCategoryLabel}
                        totalLabel="Total Forecast Revenue"
                        summaryRows={tmSummaryRows}
                      />
                    );
                  })()}
                </div>
              );
            })()}

            {projectType === "retainer" && (
              <RetainerPlaceholder />
            )}
          </TabsContent>
        </Tabs>

        {/* ── Modals ────────────────────────────────────────────────────── */}
        <OverrideLastActualsMonthModal
          open={showOverrideModal}
          currentMonth={liveLastActualsMonth}
          draftMonth={overrideDraftMonth}
          onDraftChange={setOverrideDraftMonth}
          onApply={handleOverrideApply}
          onCancel={() => setShowOverrideModal(false)}
        />

        <RollForwardModal
          open={showRollForward}
          closingMonth={closingMonth}
          nextMonth={defaultMoveTo}
          costRows={MOCK_RF_COST_ROWS}
          revenueRows={MOCK_RF_REVENUE_ROWS}
          costActions={rfCostActions}
          revenueActions={rfRevenueActions}
          onCostActionChange={handleCostActionChange}
          onRevenueActionChange={handleRevenueActionChange}
          onApply={handleApply}
          onCancel={() => setShowRollForward(false)}
        />

        {/* ── Roll-Forward modal shell (new step-based flow) ─────────────── */}
        {/* key resets all proposal-line + PM state when either mode or role switches */}
        <RollForwardModalShell
          key={`${mockBudgetMode}-${mockCurrentRole}`}
          open={rollForwardModalOpen}
          onClose={() => setRollForwardModalOpen(false)}
          proposal={activeProposal}
          isCostCentreMode={mockBudgetMode === "cost-centre"}
          currentRole={mockCurrentRole}
          onStatusChange={setProposalStatus}
        />
      </div>
    </TooltipProvider>
  );
}
